import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useActionListener, emitAction } from "@/lib/createBus";
import { useAiFocus } from "@/lib/aiFocus";
import { stripMarkdownForSpeech } from "@/lib/speechText";
import { Sparkles, Play as PlayCircle, ArrowRight, Loader2, ListOrdered, LayoutList, Focus, Check, Tag, User as UserIcon, Wand2, ChevronDown, Search, Calendar, X, SlidersHorizontal, ChevronLeft, ChevronRight as ChevronRightIcon } from "lucide-react";
import { AccountInfoTooltip } from "@/components/AccountInfoTooltip";
import AccountPicker from "@/components/AccountPicker";
import { accountDefinition } from "@/lib/accountDefinitions";
import ReclassifyPicker from "@/components/ReclassifyPicker";
import ContactPickerModal from "@/components/ContactPickerModal";
import BulkConfirmModal from "@/components/BulkConfirmModal";
import BulkUpdateModal from "@/components/BulkUpdateModal";
import { CreateRuleModal } from "@/pages/Rules";
import { toast } from "sonner";
import { reviewEta } from "@/components/DashboardTodos";

// Compact SVG donut: reviewed (emerald), ai (indigo), uncategorized (rose),
// flagged (amber), rest of total (slate). All slice sizes are proportional
// to their share of the total transaction count.
function Donut({ progress, size = 88 }) {
  const { total, reviewed = 0, ai_categorized = 0, uncategorized = 0, flagged = 0 } = progress || {};
  const rest = Math.max(0, total - reviewed - ai_categorized - uncategorized - flagged);
  const segments = [
    { v: reviewed,      c: "#10b981" },  // emerald
    { v: ai_categorized,c: "#6366f1" },  // indigo
    { v: uncategorized, c: "#f43f5e" },  // rose
    { v: flagged,       c: "#f59e0b" },  // amber
    { v: rest,          c: "#e2e8f0" },  // slate-200
  ];
  const r = 36, cx = size / 2, cy = size / 2, circ = 2 * Math.PI * r;
  let acc = 0;
  const arcs = segments.map((s, i) => {
    const frac = total ? s.v / total : 0;
    const dash = frac * circ;
    const gap = circ - dash;
    const offset = -acc * circ;
    acc += frac;
    return (
      <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.c}
              strokeWidth={12} strokeDasharray={`${dash} ${gap}`} strokeDashoffset={offset}
              transform={`rotate(-90 ${cx} ${cy})`} />
    );
  });
  const pct = progress?.pct_reviewed ?? 0;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size}>{arcs}</svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-lg font-bold text-slate-900 leading-none">{pct}%</div>
        <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">reviewed</div>
      </div>
    </div>
  );
}

const KIND_STYLES = {
  contact_in_uncat:  { chipCls: "bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100", dot: "🔥" },
  contact_split:     { chipCls: "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100", dot: "🔀" },
  contact_ai_ready:  { chipCls: "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100", dot: "✓" },
  flagged_batch:     { chipCls: "bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100", dot: "⚡" },
};

// ────────────────────────────────────────────────────────────────────
// Step-to-step navigation map. Powers the tiny prev/next arrows on
// the "Step {N}" badge card so CPAs can move between review phases
// without having to click back to the dashboard.
//   1  → Step 2
//   2  → Step 1 / Step 3A
//   3A → Step 2 / Step 3B
//   3B → Step 3A / Step 3C
//   3C → Step 3B
// Keys match `forceSubLabel || String(forceStep)`.
// ────────────────────────────────────────────────────────────────────
const STEP_NAV = {
  "1": {
    next: { href: "/accounting/lets-review?stay=1",         label: "Step 2" },
  },
  "2": {
    prev: { href: "/accounting/ai-cleanup-review?stay=1",   label: "Step 1" },
    next: { href: "/accounting/transfer-review?stay=1",     label: "Step 3A" },
  },
  "3A": {
    prev: { href: "/accounting/lets-review?stay=1",         label: "Step 2" },
    next: { href: "/accounting/no-contact-review?stay=1",   label: "Step 3B" },
  },
  "3B": {
    prev: { href: "/accounting/transfer-review?stay=1",     label: "Step 3A" },
    next: { href: "/accounting/check-register-review?stay=1", label: "Step 3C" },
  },
  "3C": {
    prev: { href: "/accounting/no-contact-review?stay=1",   label: "Step 3B" },
  },
};

// Compose a friendly one-liner the AI would say if it were a bookkeeper
// standing over your shoulder.
function pitchFor(action, progress) {
  // When there are AI-categorized rows the CPA can sign off in one tap,
  // that's the biggest single-move win — pitch that ahead of any per-
  // contact cleanup. Matches the shimmering "Approve all AI-ready" button.
  const megaReady = progress?.mega_ready_rows || 0;
  if (megaReady > 0 && progress?.total) {
    const afterPct = Math.min(
      100,
      Math.round(1000 * (progress.reviewed + megaReady) / progress.total) / 10,
    );
    return `Verifying the AI categorizations for ${megaReady} transaction${megaReady === 1 ? "" : "s"} will put you at ${afterPct}% of completing the review.`;
  }
  if (!action) {
    if (!progress?.total) return "No transactions yet — connect a bank feed or drop in a statement.";
    return "Looking clean — nothing urgent flagged.";
  }
  if (action.kind === "contact_in_uncat")
    return `${action.count} ${action.contact_name} transactions are sitting in Uncategorized — knock them out in one go?`;
  if (action.kind === "contact_split")
    return `${action.contact_name} is spread across ${action.count} accounts. Want me to help consolidate?`;
  if (action.kind === "contact_ai_ready")
    return `${action.count} ${action.contact_name} transactions were AI-categorized as ${action.account?.code} ${action.account?.name} — approve them all in one tap?`;
  if (action.kind === "flagged_batch")
    return `${action.count} transactions were flagged by the AI for a human eye. Fast-review them together?`;
  if (action.kind === "filter_uncat")
    return `${action.count} rows are still uncategorized — click Let's review to focus the table on them.`;
  if (action.kind === "filter_flagged")
    return `${action.count} rows are still flagged for review — click Let's review to focus the table on them (or Individual Review for a guided walkthrough).`;
  return action.why || action.label;
}

// Renders below the "Nothing to approve" empty state on the AI Cleanup
// Review page. Reads the dashboard checklist so we know EXACTLY what
// step comes next — Let's Review (if uncategorized-with-contact rows
// exist), Transfer Review (if intercompany pairs exist), No-Contact
// Review (if unbucketed rows remain), or Dashboard (if the whole close
// is done). The dashboard chat sometimes shows its own "next step"
// bubble, but this in-page card is the primary hand-off so the CPA
// never feels stranded after a successful mass-approve.
export function NextStepCard({ currentId, inline, onClose }) {
  const navigate = useNavigate();
  const [next, setNext] = useState(null);
  useEffect(() => {
    if (!currentId) return;
    api.get(`/companies/${currentId}/dashboard/firm-glance`).then(r => {
      const t = r.data?.todos || {};
      if ((t.step2?.count || 0) > 0) {
        setNext({
          step: 2,
          title: t.step2.title || "Let's Review",
          subtitle: t.step2.subtitle || "",
          count: t.step2.count,
          unit: t.step2.unit || "vendor groups",
          cta_link: t.step2.cta_link || "/accounting/lets-review",
        });
      } else if ((t.step3?.count || 0) > 0) {
        setNext({
          step: 3,
          title: t.step3.title || "Individual Review",
          subtitle: t.step3.subtitle || "",
          count: t.step3.count,
          unit: t.step3.unit || "transactions",
          cta_link: t.step3.cta_link || "/accounting/no-contact-review",
        });
      } else {
        setNext({ step: null });
      }
    }).catch(() => setNext({ step: null }));
  }, [currentId]);

  if (!next) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-500" data-testid="cleanup-next-step-loading">
        Checking what's next…
      </div>
    );
  }
  if (!next.step) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-5 py-4" data-testid="cleanup-next-step-done">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="text-emerald-600" size={16} />
          <div className="font-heading font-semibold text-slate-900">You've finished the cleanup checklist</div>
        </div>
        <div className="text-sm text-slate-600 mb-3">
          Nothing else needs a decision right now. Head back to the dashboard to see the updated view.
        </div>
        <button
          onClick={() => { onClose?.(); navigate("/dashboard"); }}
          data-testid="cleanup-next-step-dashboard"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium"
        >
          Back to Dashboard →
        </button>
      </div>
    );
  }
  return (
    <div className="rounded-xl border-2 border-cyan-300 bg-gradient-to-br from-cyan-50 via-white to-indigo-50 px-5 py-4 shadow-sm" data-testid={`cleanup-next-step-${next.step}`}>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="text-cyan-600" size={16} />
        <div className="font-heading font-semibold text-slate-900">
          Step {next.step} is next: {next.title}
        </div>
      </div>
      <div className="text-sm text-slate-600 mb-1">
        <span className="font-semibold text-slate-900">{next.count.toLocaleString()}</span>{" "}
        <span className="text-slate-500">{next.unit}</span> waiting for review.
      </div>
      {next.subtitle && (
        <div className="text-xs text-slate-500 mb-3">{next.subtitle}</div>
      )}
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={() => { onClose?.(); navigate(next.cta_link); }}
          data-testid={`cleanup-next-step-${next.step}-start`}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold shadow-sm"
        >
          Start Step {next.step} →
        </button>
        <button
          onClick={() => { onClose?.(); navigate("/dashboard"); }}
          data-testid={`cleanup-next-step-${next.step}-dashboard`}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm text-slate-700"
        >
          Dashboard
        </button>
      </div>
    </div>
  );
}

// StepSubtitle — renders the header subtitle with clickable inline links
// when the backend supplied `subtitle_parts` (Step 3 case: "No-contact
// transfers, transactions, & checks" — each phase word is a deep-link
// to its own substep page). Falls back to plain-text `subtitle` when
// no parts are provided (Steps 1 and 2).
function StepSubtitle({ step, onNavigate }) {
  if (step?.subtitle_parts && Array.isArray(step.subtitle_parts)) {
    return step.subtitle_parts.map((p, i) => p.href ? (
      <a
        key={i}
        href={p.href}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onNavigate?.(p.href);
        }}
        className="text-indigo-600 hover:text-indigo-800 underline decoration-dotted underline-offset-2"
        data-testid={`step-subtitle-link-${p.text}`}
      >
        {p.text}{typeof p.count === "number" ? ` (${p.count})` : ""}
      </a>
    ) : (
      <span key={i}>{p.text}</span>
    ));
  }
  return step?.subtitle || null;
}



export default function CleanupCopilot({ currentId, sampleProgress = null, onApplyAction, onStartSession, autoTrigger, inline = false, reportHeader = null, inlineTitle = null, inlineSubtitle = null, initialViewMode = null, autoStartTour = false, hideChips = false, forceStep = null, forceSubLabel = null, headerOnly = false }) {
  const navigate = useNavigate();
  const { focus } = useAiFocus();
  const { user } = useAuth();
  const [rawData, setData] = useState(null);
  const data = sampleProgress ? { ...(rawData || {}), progress: sampleProgress } : rawData;
  const [busy, setBusy] = useState(false);
  // Permanent dismissal (contact was actually handled, not just skipped).
  const [dismissed, setDismissed] = useState(new Set());
  // Ordered skip queue: contact_ids skipped in FIFO order. Skipped items are
  // moved to the BACK of the queue instead of vanishing so they come back
  // after every other contact has been seen or skipped. Reset on company
  // switch so opening another client starts with a fresh queue.
  const [skippedOrder, setSkippedOrder] = useState([]);
  // Refs mirror the two above so the async cleanup-completed handler always
  // sees the LATEST values — otherwise stale-closure reads cause the queue
  // to loop back to the just-skipped contact instead of advancing.
  const skippedOrderRef = useRef([]);
  const dismissedRef = useRef(new Set());
  useEffect(() => { skippedOrderRef.current = skippedOrder; }, [skippedOrder]);
  useEffect(() => { dismissedRef.current = dismissed; }, [dismissed]);
  useEffect(() => {
    setDismissed(new Set());
    setSkippedOrder([]);
    dismissedRef.current = new Set();
    skippedOrderRef.current = [];
  }, [currentId]);
  const [megaPreview, setMegaPreview] = useState(null);
  const [megaBusy, setMegaBusy] = useState(false);
  const [megaSelected, setMegaSelected] = useState(new Set());
  const [megaSearch, setMegaSearch] = useState("");
  // Sub-row filters — mirror the Transactions page filter bar so users
  // don't relearn a second grammar. These are applied *inside* an
  // expanded bucket's sub-row list only; the bucket list above stays
  // showing every bucket regardless of the filter values.
  //   megaSearch          — text against merchant/description/contact
  //   filterDateFrom / To — ISO date strings inclusive
  //   filterDirection     — "" | "in" (deposits) | "out" (withdrawals)
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterDirection, setFilterDirection] = useState("");
  // Bucket list view mode:
  //   "rows"     → flat list ordered by row count desc (the AI's original order)
  //   "category" → grouped by effective account code so all buckets going
  //                to e.g. "6800 · Supplies & Materials" cluster together
  const [megaViewMode, setMegaViewMode] = useState(initialViewMode || (inline ? "category" : "rows"));
  // Stepper mode: which group index is currently focused. Only used when
  // megaViewMode === "stepper". Reset to 0 whenever the mode is entered
  // or the underlying group list is refreshed.
  const [focusedGroupIdx, setFocusedGroupIdx] = useState(0);
  const [megaUndo, setMegaUndo] = useState(null);  // {batch_id, count, rules_created, expires}
  // Per-vendor category overrides: Map<contact_id, account_id>. When present,
  // the mega-approve call sends `overrides` and the vendor's row gets
  // recategorized to the target account (and snapshotted for Undo).
  const [megaOverrides, setMegaOverrides] = useState({});
  // Expand-a-bucket: clicking the "N rows" number reveals the actual
  // transactions in that bucket underneath so the CPA can edit contact
  // and category per-row before approving. Keyed by bucket.key. Values:
  // { loading, rows[], err }.
  const [expandedBucket, setExpandedBucket] = useState(null);   // bucket key
  const [expandedRows, setExpandedRows] = useState({});         // { [key]: {loading, rows, err} }
  const [contactsList, setContactsList] = useState([]);
  useEffect(() => {
    if (!currentId) return;
    api.get(`/companies/${currentId}/contacts`)
       .then(r => setContactsList(r.data.contacts || r.data || []))
       .catch(() => setContactsList([]));
  }, [currentId]);
  const toggleExpandBucket = async (bucket) => {
    if (expandedBucket === bucket.key) {
      setExpandedBucket(null);
      return;
    }
    setExpandedBucket(bucket.key);
    if (expandedRows[bucket.key]?.rows) return;   // already fetched
    setExpandedRows(prev => ({ ...prev, [bucket.key]: { loading: true } }));
    try {
      const params = new URLSearchParams();
      params.set("account_id", bucket.account?.id || "");
      if (bucket.contact_id) params.set("contact_id", bucket.contact_id);
      else params.set("merchant", bucket.contact_name || "");
      const r = await api.get(
        `/companies/${currentId}/transactions/cleanup-bucket?${params.toString()}`,
      );
      setExpandedRows(prev => ({
        ...prev, [bucket.key]: { loading: false, rows: r.data.rows || [] },
      }));
    } catch (e) {
      setExpandedRows(prev => ({
        ...prev, [bucket.key]: { loading: false, err: e.response?.data?.detail || e.message },
      }));
    }
  };
  // Force-refetch a bucket's sub-rows without collapsing it. Used after a
  // bulk action inside the expansion so the visible rows reflect the write
  // (rows that were approved should drop out of the "unreviewed" list).
  const refreshBucket = async (bucket) => {
    setExpandedRows(prev => ({ ...prev, [bucket.key]: { loading: true } }));
    try {
      const params = new URLSearchParams();
      params.set("account_id", bucket.account?.id || "");
      if (bucket.contact_id) params.set("contact_id", bucket.contact_id);
      else params.set("merchant", bucket.contact_name || "");
      const r = await api.get(
        `/companies/${currentId}/transactions/cleanup-bucket?${params.toString()}`,
      );
      setExpandedRows(prev => ({
        ...prev, [bucket.key]: { loading: false, rows: r.data.rows || [] },
      }));
    } catch (e) {
      setExpandedRows(prev => ({
        ...prev, [bucket.key]: { loading: false, err: e.response?.data?.detail || e.message },
      }));
    }
  };
  const updateBucketRow = async (bucketKey, txnId, patch) => {
    // Optimistic — patch in-place so the picker doesn't blank while the
    // request is in flight. Revert on error.
    setExpandedRows(prev => {
      const b = prev[bucketKey]; if (!b?.rows) return prev;
      return { ...prev, [bucketKey]: {
        ...b, rows: b.rows.map(r => r.id === txnId ? { ...r, ...patch } : r),
      }};
    });
    try {
      await api.patch(`/companies/${currentId}/transactions/${txnId}`, patch);
    } catch (e) {
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: `Update failed: ${e.response?.data?.detail || e.message}`, type: "error" } }));
    }
  };
  const bulkUpdateBucketRows = async (bucketKey, txnIds, patch) => {
    // Optimistic bulk patch — apply the same `patch` to every selected
    // row in-memory, then fire the matching bulk endpoint. Rolls back
    // on error by refetching the bucket.
    setExpandedRows(prev => {
      const b = prev[bucketKey]; if (!b?.rows) return prev;
      const idSet = new Set(txnIds);
      const rowPatch = { ...patch };
      delete rowPatch.kind;
      return { ...prev, [bucketKey]: {
        ...b, rows: b.rows.map(r => idSet.has(r.id) ? { ...r, ...rowPatch } : r),
      }};
    });
    try {
      if (patch.kind === "contact") {
        await api.post(`/companies/${currentId}/transactions/bulk-set-contact`, {
          transaction_ids: txnIds,
          contact_id: patch.contact_id,
        });
      } else if (patch.kind === "category") {
        await api.post(`/companies/${currentId}/transactions/bulk-reclassify`, {
          transaction_ids: txnIds,
          category_account_id: patch.category_account_id,
        });
      }
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: `Updated ${txnIds.length} rows`, type: "success" } }));
    } catch (e) {
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: `Bulk update failed: ${e.response?.data?.detail || e.message}`, type: "error" } }));
    }
  };
  // Guided-tour ("How To") state — non-null means a walkthrough is running
  // and this step's UI element gets the rainbow highlight. `howToTargetKey`
  // holds the vendor row we're using as the demo subject (the first bucket
  // in the preview) so we can highlight sub-elements of that specific row.
  const [howToStep, setHowToStep] = useState(null);
  const [howToRunning, setHowToRunning] = useState(false);
  const [howToTargetKey, setHowToTargetKey] = useState(null);
  const howToAbortRef = useRef(false);
  // Full CoA for the current company, loaded once when the modal opens. Used
  // for the category dropdown and the info-icon tooltip.
  const [accounts, setAccounts] = useState([]);
  // Auto-create rules on approval? User-controllable, persisted per-browser.
  const [autoCreateRules, setAutoCreateRules] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("axiom.mega.autoCreateRules");
    return v === null ? true : v === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("axiom.mega.autoCreateRules", autoCreateRules ? "1" : "0");
    }
  }, [autoCreateRules]);
  useEffect(() => {
    // Auto-dismiss the Undo toast after 60s.
    if (!megaUndo) return;
    const t = setTimeout(() => setMegaUndo(null), 60_000);
    return () => clearTimeout(t);
  }, [megaUndo]);
  const openMega = async () => {
    if (megaBusy || !currentId) return;
    setMegaBusy(true);
    try {
      const [r, ar] = await Promise.all([
        api.post(
          `/companies/${currentId}/transactions/bulk-approve-ai-ready`,
          { dry_run: true }
        ),
        api.get(`/companies/${currentId}/accounts`),
      ]);
      // Filter out uncategorized sinks so they never appear in the override
      // dropdown — the whole point of the modal is to *get out* of them.
      const allAccounts = (ar.data?.accounts || []).filter(
        a => !["9999", "6999", "4999"].includes(String(a.code))
      );
      setAccounts(allAccounts);
      setMegaOverrides({});
      if (!r.data?.total_rows) {
        setMegaPreview({ total_rows: 0, vendors: [] });
        setMegaSelected(new Set());
      } else {
        setMegaPreview(r.data);
        // Everyone selected by default — CPA can uncheck the risky ones.
        // Selection key is the bucket key ("<contact_id>::<account_id>").
        setMegaSelected(new Set((r.data.vendors || []).map(v => v.key)));
      }
      setMegaSearch("");
    } catch (e) {
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: "Couldn't scan AI-ready rows. Try again in a moment.", type: "error" } }));
    } finally { setMegaBusy(false); }
  };
  const applyMega = async () => {
    if (megaBusy || !currentId || megaSelected.size === 0) return;
    setMegaBusy(true);
    try {
      // Only send overrides for buckets actually being approved this round.
      const overrides = {};
      for (const k of megaSelected) {
        if (megaOverrides[k]) overrides[k] = megaOverrides[k];
      }
      const r = await api.post(
        `/companies/${currentId}/transactions/bulk-approve-ai-ready`,
        {
          dry_run: false,
          keys: Array.from(megaSelected),
          auto_create_rules: autoCreateRules,
          ...(Object.keys(overrides).length ? { overrides } : {}),
        }
      );
      setMegaPreview(null);
      setMegaSelected(new Set());
      // Reset the inline auto-open guard so the report page re-fetches
      // and re-opens on the leftover uncategorized rows. Without this,
      // clicking "Approve X rows" cleared the preview and the effect at
      // line ~692 short-circuited (inlineOpenRef stuck at true) — the
      // page went blank below the copilot header until the user
      // reloaded. See bug repro at /accounting/ai-cleanup-review?view=
      // stepper on 1213 LLC after Approve.
      inlineOpenRef.current = false;
      load();
      window.dispatchEvent(new CustomEvent("axiom:action",
        { detail: { kind: "txns:changed", at: Date.now() } }));
      // Set the Undo toast — visible for 60s.
      if (r.data?.batch_id && r.data?.updated) {
        setMegaUndo({
          batch_id: r.data.batch_id,
          count: r.data.updated,
          rules_created: (r.data.rules_created || []).length,
          expires: Date.now() + 60_000,
        });
      }
    } catch (e) {
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: "Bulk-approve failed. No rows were changed.", type: "error" } }));
    } finally { setMegaBusy(false); }
  };
  const undoMega = async () => {
    if (!megaUndo || !currentId) return;
    const snapshot = megaUndo;
    setMegaUndo(null);
    try {
      const r = await api.post(
        `/companies/${currentId}/transactions/undo-mega-batch/${snapshot.batch_id}`, {}
      );
      load();
      window.dispatchEvent(new CustomEvent("axiom:action",
        { detail: { kind: "txns:changed", at: Date.now() } }));
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: `Reverted ${r.data?.reverted || 0} rows.` } }));
    } catch (e) {
      // Restore the toast so the user can retry Undo instead of being locked out.
      setMegaUndo(snapshot);
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: "Undo failed — try again.", type: "error" } }));
    }
  };
  const approveOne = async (vendor) => {
    if (!currentId) return;
    // Optimistic remove so the CPA can fly through the list.
    setMegaPreview(mp => mp ? {
      ...mp,
      vendors: mp.vendors.filter(v => v.key !== vendor.key),
      total_rows: mp.total_rows - vendor.count,
      total_buckets: (mp.total_buckets || 0) - 1,
      total_amount: mp.total_amount - vendor.amount,
    } : mp);
    setMegaSelected(prev => {
      const n = new Set(prev); n.delete(vendor.key); return n;
    });
    try {
      const overrideId = megaOverrides[vendor.key];
      const r = await api.post(
        `/companies/${currentId}/transactions/bulk-approve-ai-ready`,
        {
          dry_run: false,
          keys: [vendor.key],
          auto_create_rules: autoCreateRules,
          ...(overrideId ? { overrides: { [vendor.key]: overrideId } } : {}),
        }
      );
      window.dispatchEvent(new CustomEvent("axiom:action",
        { detail: { kind: "txns:changed", at: Date.now() } }));
      if (r.data?.batch_id && r.data?.updated) {
        setMegaUndo({
          batch_id: r.data.batch_id,
          count: r.data.updated,
          rules_created: (r.data.rules_created || []).length,
          expires: Date.now() + 60_000,
        });
      }
    } catch (e) {
      // Roll back the optimistic remove on failure.
      setMegaPreview(mp => mp ? {
        ...mp,
        vendors: [vendor, ...mp.vendors],
        total_rows: mp.total_rows + vendor.count,
        total_buckets: (mp.total_buckets || 0) + 1,
        total_amount: mp.total_amount + vendor.amount,
      } : mp);
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: `Couldn't approve ${vendor.contact_name}. Try again.`, type: "error" } }));
    }
  };
  const scrollToTop = () => {
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* ie11 fallback */ window.scrollTo(0, 0); }
  };
  const gotoPrev = () => { setFocusedGroupIdx(i => Math.max(0, i - 1)); scrollToTop(); };
  const gotoNext = () => { setFocusedGroupIdx(i => Math.min((megaGroups?.length || 1) - 1, i + 1)); scrollToTop(); };

  const toggleVendor = (key) => {
    setMegaSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Approve every bucket in a category group in one request. Same
  // optimistic-then-rollback pattern as `approveOne` — we build the
  // keys array + overrides map from the group's vendors, remove them
  // from the modal state up-front, and roll back on failure.
  const approveGroup = async (group) => {
    if (!currentId || !group?.vendors?.length) return;
    const vendors = group.vendors;
    const keySet = new Set(vendors.map(v => v.key));
    const overrides = {};
    for (const v of vendors) {
      const o = megaOverrides[v.key];
      if (o) overrides[v.key] = o;
    }
    // Optimistic pop.
    const removedTotals = vendors.reduce(
      (acc, v) => {
        acc.rows += v.count;
        acc.amt += v.amount;
        return acc;
      },
      { rows: 0, amt: 0 },
    );
    setMegaPreview(mp => mp ? {
      ...mp,
      vendors: mp.vendors.filter(v => !keySet.has(v.key)),
      total_rows: mp.total_rows - removedTotals.rows,
      total_buckets: (mp.total_buckets || 0) - vendors.length,
      total_amount: mp.total_amount - removedTotals.amt,
    } : mp);
    setMegaSelected(prev => {
      const n = new Set(prev);
      for (const k of keySet) n.delete(k);
      return n;
    });
    try {
      const r = await api.post(
        `/companies/${currentId}/transactions/bulk-approve-ai-ready`,
        {
          dry_run: false,
          keys: Array.from(keySet),
          auto_create_rules: autoCreateRules,
          ...(Object.keys(overrides).length ? { overrides } : {}),
        }
      );
      window.dispatchEvent(new CustomEvent("axiom:action",
        { detail: { kind: "txns:changed", at: Date.now() } }));
      if (r.data?.batch_id && r.data?.updated) {
        setMegaUndo({
          batch_id: r.data.batch_id,
          count: r.data.updated,
          rules_created: (r.data.rules_created || []).length,
          expires: Date.now() + 60_000,
        });
      }
      // Stepper: the approved group is filtered out — the same index
      // now points at the next group. Clamp so we don't overshoot the end.
      if (megaViewMode === "stepper") {
        setFocusedGroupIdx(idx => Math.max(0, Math.min(idx, (megaGroups?.length || 1) - 2)));
      }
    } catch (e) {
      // Roll back — put vendors back at the top and re-select them.
      setMegaPreview(mp => mp ? {
        ...mp,
        vendors: [...vendors, ...mp.vendors],
        total_rows: mp.total_rows + removedTotals.rows,
        total_buckets: (mp.total_buckets || 0) + vendors.length,
        total_amount: mp.total_amount + removedTotals.amt,
      } : mp);
      window.dispatchEvent(new CustomEvent("axiom:toast",
        { detail: { message: `Couldn't approve group. Try again.`, type: "error" } }));
    }
  };

  const load = async () => {
    if (!currentId) return;
    setBusy(true);
    try {
      const r = await api.get(`/companies/${currentId}/transactions/cleanup-suggestions`);
      setData(r.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentId]);

  // Feb 2026 — the voice copilot fires `txns:changed` after every
  // apply-categorize-proposal / mark-as-transfer / approve. The bucket
  // list on this page was previously not listening, so a "yes"
  // reclassify from the AiPanel would move the ledger but the bucket
  // card kept showing the old category. Refetch on every change.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; });
  useActionListener("txns:changed", async () => {
    // Refetch bucket list + refresh accounts list so newly-created
    // accounts (from AI create-then-categorize flow) show up in the
    // dropdowns and are available for the next fuzzy-match. Ignores
    // errors so a transient failure doesn't break the reload.
    loadRef.current?.();
    try {
      const ar = await api.get(`/companies/${currentIdRefApply.current}/accounts`);
      const allAccounts = (ar.data?.accounts || []).filter(
        a => !["9999", "6999", "4999"].includes(String(a.code))
      );
      setAccounts(allAccounts);
    } catch { /* non-fatal */ }
  });

  // Feb 2026 — AI Cleanup Review page listener for the voice
  // "apply-categorize-proposal" action. Previously this listener
  // ONLY existed on the Transactions page, so voice reclassify said
  // "On it..." but never actually reclassified. Now we resolve the
  // focused bucket to its rows and fire the same bulk-approve-ai-
  // ready endpoint with an override to the target account. Idempotent
  // — safe to retry on failure.
  const megaPreviewRefApply = useRef(megaPreview);
  useEffect(() => { megaPreviewRefApply.current = megaPreview; }, [megaPreview]);
  const accountsRefApply = useRef(accounts);
  useEffect(() => { accountsRefApply.current = accounts; }, [accounts]);
  const focusRefApply = useRef(focus);
  useEffect(() => { focusRefApply.current = focus; }, [focus]);
  const currentIdRefApply = useRef(currentId);
  useEffect(() => { currentIdRefApply.current = currentId; }, [currentId]);
  const megaSelectedRefApply = useRef(megaSelected);
  useEffect(() => { megaSelectedRefApply.current = megaSelected; }, [megaSelected]);
  useActionListener("apply-categorize-proposal", async (payload) => {
    if (!payload?.category && !payload?.accountId) return;
    const cid = currentIdRefApply.current;
    if (!cid) return;
    const preview = megaPreviewRefApply.current;
    const focusVal = focusRefApply.current;
    // Feb 2026 — When the caller (AiPanel create-then-categorize handler)
    // just created a new CoA account and knows its id, prefer that over
    // fuzzy-matching against our locally-cached accounts list (which is
    // loaded once on mount and can be stale). Falls back to fuzzy match
    // for the ordinary categorize-proposal path.
    let match = null;
    if (payload.accountId) {
      match = {
        id: payload.accountId,
        name: payload.category || payload.accountName || "the new account",
      };
    } else {
      const acctList = (accountsRefApply.current || []).filter(a => !a.retired_at);
      const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
      const needle = norm(payload.category);
      match = acctList.find(a => norm(a.name) === needle);
      if (!match) match = acctList.find(a => norm(a.name).includes(needle));
      // Feb 2026 — no loose "needle contains name" fallback (see
      // Transactions.jsx apply-categorize-proposal for the same fix).
      if (!match) {
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: `No account named "${payload.category}" in your Chart of Accounts. Ask the AI to create one.`, type: "error" } }));
        return;
      }
    }
    // Prefer the pinned bucket focus, then fall back to CHECKED
    // buckets (the mega-approve selection), then fall back to a
    // single txn focus. Feb 2026 — added the "checked buckets"
    // fallback because users naturally check a set of buckets and
    // say "these are furniture" without pinning a single focus. The
    // "no focus" toast was silently dropping their intent.
    const selected = megaSelectedRefApply.current;
    if (focusVal?.bucket && focusVal.key && preview) {
      const bucketKey = focusVal.key;
      const vendor = (preview.vendors || []).find(v => v.key === bucketKey);
      if (!vendor) {
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: "Focused bucket is no longer in the review queue.", type: "error" } }));
        return;
      }
      try {
        const r = await api.post(
          `/companies/${cid}/transactions/bulk-approve-ai-ready`,
          {
            dry_run: false,
            keys: [bucketKey],
            auto_create_rules: false,
            overrides: { [bucketKey]: match.id },
          }
        );
        window.dispatchEvent(new CustomEvent("axiom:action",
          { detail: { kind: "txns:changed", at: Date.now() } }));
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: `Reclassified ${r.data?.updated || 0} rows to ${match.name}.`, type: "success" } }));
      } catch (e) {
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: `Reclassify failed: ${e?.response?.data?.detail || e.message}`, type: "error" } }));
      }
      return;
    }
    // Checked-bucket batch fallback — applies the target account to
    // every currently-checked bucket via the mega-approve endpoint's
    // per-bucket override map. This is the most common user flow:
    // check the boxes for the furniture stores, then say "these are
    // furniture" and confirm.
    if (selected && selected.size > 0 && preview) {
      const keys = Array.from(selected).filter(k =>
        (preview.vendors || []).some(v => v.key === k),
      );
      if (keys.length === 0) {
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: "Checked buckets are no longer in the queue.", type: "error" } }));
        return;
      }
      const overrides = {};
      for (const k of keys) overrides[k] = match.id;
      try {
        const r = await api.post(
          `/companies/${cid}/transactions/bulk-approve-ai-ready`,
          {
            dry_run: false,
            keys,
            auto_create_rules: false,
            overrides,
          }
        );
        window.dispatchEvent(new CustomEvent("axiom:action",
          { detail: { kind: "txns:changed", at: Date.now() } }));
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: `Reclassified ${r.data?.updated || 0} rows across ${keys.length} bucket${keys.length === 1 ? "" : "s"} to ${match.name}.`, type: "success" } }));
      } catch (e) {
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: `Reclassify failed: ${e?.response?.data?.detail || e.message}`, type: "error" } }));
      }
      return;
    }
    // Single-txn focus fallback (mirrors the Transactions-page path).
    if (payload.focusedTxnId) {
      try {
        await api.patch(`/companies/${cid}/transactions/${payload.focusedTxnId}`, {
          category_account_id: match.id,
        });
        window.dispatchEvent(new CustomEvent("axiom:action",
          { detail: { kind: "txns:changed", at: Date.now() } }));
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: `Recategorized to ${match.name}.`, type: "success" } }));
      } catch (e) {
        window.dispatchEvent(new CustomEvent("axiom:toast",
          { detail: { message: `Recategorize failed: ${e?.response?.data?.detail || e.message}`, type: "error" } }));
      }
      return;
    }
    window.dispatchEvent(new CustomEvent("axiom:toast",
      { detail: { message: "Focus a bucket or hover a row first.", type: "error" } }));
  });

  // Fetch the dashboard checklist so the copilot header can show a compact
  // "STEP N · count" badge alongside the CTAs. Piggybacks on the same
  // firm-glance endpoint the dashboard tiles use — cheap and cached
  // server-side per (cid, month).
  const [checklistTodos, setChecklistTodos] = useState(null);
  useEffect(() => {
    if (!currentId) return;
    api.get(`/companies/${currentId}/dashboard/firm-glance`)
      .then((r) => setChecklistTodos(r.data?.todos || null))
      .catch(() => setChecklistTodos(null));
  }, [currentId, data]);
  // Step 1 badge shows a time-to-finish estimate (same formula as the
  // dashboard: ~15 min per 1,300 unapproved rows) and ticks down as rows
  // get approved — refetched on every txns:changed.
  const [unapprovedCount, setUnapprovedCount] = useState(null);
  const [etaTick, setEtaTick] = useState(0);
  useActionListener("txns:changed", () => setEtaTick((t) => t + 1));
  useEffect(() => {
    if (!currentId) return;
    let cancelled = false;
    api.get(`/companies/${currentId}/transactions?status=unapproved&limit=1`)
      .then((r) => !cancelled && setUnapprovedCount(r.data?.pagination?.total ?? 0))
      .catch(() => !cancelled && setUnapprovedCount(null));
    return () => { cancelled = true; };
  }, [currentId, data, etaTick]);
  // Pick the earliest step still open (count > 0). That's the step the CPA
  // is currently on — matches the dashboard checklist numbering.
  // Pick the step to show in the header badge:
  //   • If `forceStep` prop is set, always show that step (used on the
  //     AI Cleanup Review page, which is definitionally Step 1 — the
  //     badge should read "Step 1" even if the CPA has already burned
  //     Step 1 down to zero).
  //   • Otherwise, pick the earliest step still open (count > 0) —
  //     matches the dashboard checklist numbering.
  // Chat-review mode awareness — the dashboard todos widget lets the
  // CPA flip into "chat" mode (persisted in localStorage under
  // "dashboard-todos-mode"). When they do, the clickable Step-N card
  // on the Transactions page should route into the chat-review
  // surface instead of the checklist review pages, so the user stays
  // in whichever review UX they picked.
  const [chatModeOn, setChatModeOn] = useState(() => {
    try { return localStorage.getItem("dashboard-todos-mode") === "chat"; }
    catch { return false; }
  });
  // "AI Transaction Review" dashboard mode: Step 1 is worked right here on
  // the Transactions page (To do filter) — never the cleanup-review tour.
  const [aiModeOn, setAiModeOn] = useState(() => {
    try { return localStorage.getItem("dashboard-todos-mode") === "ai"; }
    catch { return false; }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "dashboard-todos-mode") {
        setChatModeOn(e.newValue === "chat");
        setAiModeOn(e.newValue === "ai");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  // When chat-review mode is active, fetch the chat-review-queue so
  // the header badge can show live "X questions · Y checks" totals
  // plus a dollar-based progress ring, instead of the checklist-mode
  // "Review N categories" copy that would otherwise leak from the
  // firm-glance step1 payload.
  const [chatQueue, setChatQueue] = useState(null);
  useEffect(() => {
    if (!chatModeOn || !currentId) { setChatQueue(null); return; }
    let cancel = false;
    api.get(`/companies/${currentId}/reviewv2/chat-review-queue`)
      .then((r) => { if (!cancel) setChatQueue(r.data || null); })
      .catch(() => { if (!cancel) setChatQueue(null); });
    return () => { cancel = true; };
  }, [chatModeOn, currentId, data]);

  const activeStep = (() => {
    if (!checklistTodos) return null;
    const pickers = forceStep
      ? [forceStep]
      : [1, 2, 3];
    for (const n of pickers) {
      const s = checklistTodos[`step${n}`];
      if (!s) continue;
      if (!forceStep && (s.count || 0) === 0) continue;
      // When forceSubLabel is set (e.g. rendering the badge on a
      // dedicated sub-step page like Step 3C = check register review),
      // override the display letter and swap the badge count/unit to
      // match that phase's own numbers instead of the aggregate.
      const subLabel = forceSubLabel || s.sub_label || String(n);
      const subOverrides = (() => {
        if (!forceSubLabel) return null;
        if (forceSubLabel === "3A") return { count: s.transfer_pairs_count || 0, unit: "pairs" };
        if (forceSubLabel === "3B") return { count: s.no_contact_count || 0, unit: "transactions" };
        if (forceSubLabel === "3C") return { count: s.check_count || 0, unit: "checks" };
        return null;
      })();
      const etaFor1 = n === 1 && !subOverrides && unapprovedCount != null && unapprovedCount > 0;
      return {
        n,
        // Step 1: headline is time-to-finish; raw count moves to a sub-line.
        eta: etaFor1 ? { value: reviewEta(unapprovedCount), unit: "to finish" } : null,
        // sub_label = "3A"/"3B" when firm-glance splits a step into
        // sub-phases; fall back to the numeric n for steps 1/2 which
        // aren't split.
        display: subLabel,
        title: s.title,
        subtitle: s.subtitle || "",
        // Structured link tokens for Step 3 (feb 2026) — each phase
        // word is a clickable inline link to the other substep pages
        // so the CPA can jump between transfers / no-contact / checks
        // without going back to the dashboard.
        subtitle_parts: s.subtitle_parts || null,
        count: subOverrides ? subOverrides.count : (s.count || 0),
        unit: subOverrides ? subOverrides.unit : s.unit,
        cta_link: s.cta_link || "",
      };
    }
    return null;
  })();

  // Auto-advance: when the AiPanel confirms a cleanup batch is done, dismiss
  // that contact from our queue, reload the actions list, and — if there's
  // another Fix Now candidate — automatically kick it off so the user rolls
  // through the queue without touching the copilot again.
  const onApplyRef = useRef(onApplyAction);
  useEffect(() => { onApplyRef.current = onApplyAction; });

  // Refs for the mega-approve listener below — allows the AI voice
  // command "approve this" (with a bucket focus) to fire this same
  // function without stale-closure issues from the empty-deps listener.
  const approveOneRef = useRef(() => {});
  const megaPreviewRef = useRef(null);
  useEffect(() => { approveOneRef.current = approveOne; });
  useEffect(() => { megaPreviewRef.current = megaPreview; });
  useActionListener("mega-approve-bucket", (payload) => {
    const key = payload?.key;
    if (!key) return;
    const preview = megaPreviewRef.current;
    if (!preview) return;
    const vendor = (preview.vendors || []).find(v => v.key === key);
    if (vendor) approveOneRef.current(vendor);
  });

  // ---- Guided "How To" tour ----------------------------------------
  // The user hits "How To" at the top of the mega-approve modal and the
  // AI narrates through the screen, highlighting each element as it goes.
  // We use browser TTS directly so we can await utterance-end and step
  // forward at natural pauses.
  const HOWTO_STEPS = [
    { text: "These are all the transactions the AI has already categorized for you, grouped by vendor. Let's walk through what you can do here." },
    { text: "The green checkbox on the left means this vendor's transactions are included in the batch approval. Uncheck any vendor you want to exclude." },
    { text: "Next to the vendor name is the category the AI picked. If you disagree, click the dropdown to change how this vendor's transactions will be booked." },
    { text: "The small info icon opens a tooltip explaining what that category means in plain English — handy when you're not sure whether something belongs in Supplies or Cost of Goods." },
    { text: "To the right you can see how many transactions this vendor covers, along with the total dollar amount they represent." },
    { text: "Click the sparkle icon if you want to ask the AI a question about this vendor's categorization or the transactions themselves. It focuses the AI on that bucket so you can just start talking." },
    { text: "You can also approve any individual bucket right here without waiting to reach the bottom — just click the Approve arrow next to the row." },
    { text: "When you're ready, hit the big green Approve button at the bottom. That'll approve every checked vendor at once, and if the auto-create rules toggle is on, it also builds rules so future transactions from these vendors land in the right place automatically. That's usually 80% or more of your cleanup, done in one click." },
  ];
  const cancelHowTo = () => {
    howToAbortRef.current = true;
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    setHowToStep(null);
    setHowToRunning(false);
    setHowToTargetKey(null);
  };
  const speakAsync = (text, minMs = 3200) => new Promise(resolve => {
    // Always hold the step for at least `minMs` regardless of TTS
    // behavior so the highlight has time to be seen. When the user has
    // muted narration (or the browser is silent for any reason) the
    // utterance's `onend` fires instantly, which used to race through
    // every step in < 1s and made it feel like nothing was highlighting.
    const start = Date.now();
    const finish = () => {
      const elapsed = Date.now() - start;
      const wait = Math.max(0, minMs - elapsed);
      setTimeout(resolve, wait);
    };
    let userMuted = false;
    try { userMuted = localStorage.getItem("axiom_tts") === "0"; } catch { /* ignore */ }
    if (typeof window === "undefined" || !window.speechSynthesis || userMuted) return finish();
    const clean = stripMarkdownForSpeech(text);
    if (!clean) return finish();
    const u = new SpeechSynthesisUtterance(clean);
    u.rate = 1.02; u.pitch = 1.0;
    // Prefer the "Google UK English Female (en-GB)" voice — warm, friendly
    // narration for the tour. Fall back gracefully to en-GB female /
    // en-US female / default if that specific voice isn't loaded.
    const voices = window.speechSynthesis.getVoices() || [];
    const pick =
      voices.find(v => v.name === "Google UK English Female") ||
      voices.find(v => v.name.toLowerCase().includes("uk english female")) ||
      voices.find(v => v.lang === "en-GB" && /female/i.test(v.name)) ||
      voices.find(v => v.lang === "en-GB") ||
      voices.find(v => v.lang?.startsWith("en") && /female/i.test(v.name)) ||
      null;
    if (pick) u.voice = pick;
    u.onend = finish; u.onerror = finish;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  });
  const runHowTo = async () => {
    if (howToRunning) return;
    const preview = megaPreviewRef.current;
    if (!preview || !preview.vendors?.length) return;
    // Preload voices — some browsers return an empty array on the first
    // getVoices() call and only populate after `voiceschanged` fires. Warm
    // it up here so the very first utterance already has the right voice.
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      if (window.speechSynthesis.getVoices().length === 0) {
        await new Promise(r => {
          const t = setTimeout(r, 400);
          window.speechSynthesis.addEventListener("voiceschanged", () => {
            clearTimeout(t); r();
          }, { once: true });
        });
      }
    }
    // Anchor the highlight to the FIRST vendor of the CURRENTLY-VISIBLE
    // group in stepper view — otherwise a user viewing group 3 sees a
    // tour that quietly highlights an off-screen row in group 1 and
    // wonders why "nothing is happening". In category/list view we
    // fall back to `preview.vendors[0]` (top of the list).
    let anchorVendorKey = preview.vendors[0].key;
    try {
      if (megaViewMode === "stepper" && Array.isArray(megaGroups) && megaGroups.length > 0) {
        const g = megaGroups[Math.min(focusedGroupIdx, megaGroups.length - 1)];
        if (g && g.vendors && g.vendors.length > 0) {
          anchorVendorKey = g.vendors[0].key;
        }
      }
    } catch { /* fall through with preview.vendors[0] */ }
    setHowToTargetKey(anchorVendorKey);
    setHowToRunning(true);
    howToAbortRef.current = false;
    // Make sure the AI panel is visible so the narration lands somewhere.
    emitAction("ai-open");
    for (let i = 0; i < HOWTO_STEPS.length; i++) {
      if (howToAbortRef.current) break;
      setHowToStep(i);
      // Post the narration into the AI chat as an assistant bubble too so
      // there's a written trail after the tour ends.
      emitAction("ai-chat-say", { message: HOWTO_STEPS[i].text });
      // Give each step at least ~50ms/char (roughly TTS speaking rate)
      // so muted users still see the highlight linger on the element
      // being described.
      const minMs = Math.max(3200, HOWTO_STEPS[i].text.length * 55);
      await speakAsync(HOWTO_STEPS[i].text, minMs);
      if (howToAbortRef.current) break;
      // Brief beat between steps so the highlight doesn't snap instantly.
      await new Promise(r => setTimeout(r, 400));
    }
    setHowToStep(null);
    setHowToRunning(false);
    setHowToTargetKey(null);
    // Post a final assistant bubble with a "Re-play tour" CTA so users
    // who dismissed the narration on autopilot can rewind without
    // hunting for the toolbar's "How To" button. AiPanel renders the
    // button; clicking it emits `chat-cta:restart-tour`, which we
    // listen for below to re-run the walkthrough.
    if (!howToAbortRef.current) {
      emitAction("ai-chat-say-with-cta", {
        message: "That's the whole tour. Ready to review your books.",
        cta: { label: "Re-play tour", actionKey: "restart-tour" },
      });
    }
  };

  // "Re-play tour" button in the AI chat panel → re-run the walkthrough.
  useActionListener("chat-cta:restart-tour", () => {
    if (howToRunning) return;
    runHowTo();
  });

  // Auto-start the "How To" walkthrough on entry when the parent asked us
  // to (via `autoStartTour` prop, wired to `?tour=1` from Setup checklist
  // Step 1). First-run only — once the tour plays for a given user+company
  // pair, we persist `tour_seen:<uid>:<cid>` in localStorage and skip on
  // subsequent visits so CPAs juggling many clients aren't re-narrated.
  // The manual "How To" button in the toolbar still always works.
  const autoTourFiredRef = useRef(false);
  useEffect(() => {
    if (!autoStartTour) return;
    if (autoTourFiredRef.current) return;
    if (howToRunning) return;
    const vendors = megaPreview?.vendors || [];
    if (vendors.length === 0) return;
    if (!currentId || !user?.id) return;
    const seenKey = `tour_seen:${user.id}:${currentId}`;
    try {
      if (localStorage.getItem(seenKey) === "1") return;
    } catch { /* localStorage disabled — just play the tour */ }
    autoTourFiredRef.current = true;
    // Give React a beat to render the vendor rows before the tour tries
    // to highlight the first one.
    setTimeout(() => {
      runHowTo();
      // Stamp AFTER kickoff so an aborted mid-tour doesn't accidentally
      // preserve "unseen" state — user still gets the guided experience
      // once, and can re-trigger via the manual button if they want it.
      try { localStorage.setItem(seenKey, "1"); } catch { /* ignore */ }
    }, 500);
  }, [autoStartTour, megaPreview, howToRunning, currentId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useActionListener("cleanup-completed", async (payload) => {
    const cid = payload?.contact_id;
    const wasSkip = !!payload?.skipped;
    // Compute the next skip queue + dismissed set BEFORE reading state, using
    // refs so we always see the freshest values (React state updates are
    // async — the closure could otherwise read stale data and loop back to
    // the just-skipped contact).
    let nextSkipOrder = skippedOrderRef.current.slice();
    const nextDismissed = new Set(dismissedRef.current);
    if (cid) {
      if (wasSkip) {
        nextSkipOrder = nextSkipOrder.filter(x => x !== cid);
        nextSkipOrder.push(cid);
      } else {
        nextDismissed.add(`contact_in_uncat-${cid}`);
        nextDismissed.add(`contact_split-${cid}`);
        nextDismissed.add(`contact_ai_ready-${cid}`);
        nextSkipOrder = nextSkipOrder.filter(x => x !== cid);
      }
      // Commit to state + refs. Refs first so any *synchronous* re-entry
      // during the same tick sees the new values.
      skippedOrderRef.current = nextSkipOrder;
      dismissedRef.current = nextDismissed;
      setSkippedOrder(nextSkipOrder);
      setDismissed(nextDismissed);
    }
    // Reload the actions list so recently-cleared contacts drop off.
    setBusy(true);
    let latest = null;
    try {
      const r = await api.get(`/companies/${currentId}/transactions/cleanup-suggestions`);
      latest = r.data;
      setData(latest);
    } finally { setBusy(false); }

    // Reorder: unseen contacts first, then skipped contacts in FIFO skip
    // order at the back. Only auto-advance if there's a NEW (never-skipped)
    // contact — never bounce straight back into the skip ring, otherwise a
    // 2-item queue re-serves the just-skipped item immediately.
    const eligible = (latest?.top_actions || []).filter(a => {
      if (a.kind === "flagged_batch") return false;
      const key = `${a.kind}-${a.contact_id || a.count}`;
      return !nextDismissed.has(key);
    });
    const unseen = eligible.filter(
      a => !a.contact_id || !nextSkipOrder.includes(a.contact_id)
    );
    const skippedRing = nextSkipOrder
      .map(scid => eligible.find(a => a.contact_id === scid))
      .filter(Boolean);
    const ordered = [...unseen, ...skippedRing];
    // Pick the first UNSEEN action first. Only fall back to a resurfaced-
    // skipped one when there are no unseen contacts left. Also guard against
    // instantly picking the just-skipped contact.
    let next = unseen[0];
    if (!next) {
      next = skippedRing.find(a => a.contact_id !== cid) || skippedRing[0];
    }
    if (next && next.contact_id === cid) {
      // Absolute safety net: don't re-serve the same contact we just skipped
      // on this turn. Take the next one in the ring instead.
      next = ordered.find(a => a.contact_id !== cid) || null;
    }
    if (next) {
      setTimeout(() => { onApplyRef.current?.(next); }, 1200);
    }
  });

  // Reorder actions so skipped contacts fall to the BACK (in FIFO skip
  // order). Permanent dismissals are filtered out first. `flagged_batch`
  // is deliberately excluded from the primary/rest queue — it powers the
  // one-at-a-time review, which is the "Start 5-min session" button, not
  // "Fix now" (Fix now is contact-scoped bulk cleanup only).
  const eligibleActions = (data?.top_actions || []).filter(a =>
    a.kind !== "flagged_batch"
    && !dismissed.has(`${a.kind}-${a.contact_id || a.count}`)
  );
  const unseenActions = eligibleActions.filter(
    a => !a.contact_id || !skippedOrder.includes(a.contact_id)
  );
  const skippedActions = skippedOrder
    .map(scid => eligibleActions.find(a => a.contact_id === scid))
    .filter(Boolean);
  const actions = [...unseenActions, ...skippedActions];
  // Fallback synthetic primary when no contact-scoped cleanup exists but
  // there IS still work to do — filters the transactions table to the
  // right tab so the CPA can dig in manually. This is what keeps Fix now
  // visible on companies like Bright Beans where the queue is drained of
  // per-contact clusters but flagged / uncategorized rows still linger.
  const synthPrimary = (() => {
    if (actions.length > 0) return null;
    const p = data?.progress;
    if (!p) return null;
    if (p.uncategorized > 0) {
      return {
        kind: "filter_uncat",
        count: p.uncategorized,
        label: `${p.uncategorized} uncategorized rows`,
      };
    }
    if (p.flagged > 0) {
      return {
        kind: "filter_flagged",
        count: p.flagged,
        label: `${p.flagged} flagged rows`,
      };
    }
    return null;
  })();
  const primary = actions[0] || synthPrimary;
  const rest = actions.slice(1, 6);
  const total = data?.progress?.total || 0;

  // Auto-trigger: when the user lands here via the Dashboard's "Flagged for
  // review" card (which adds `?auto=1`), pick the SAME action the shimmering
  // CTA would run:
  //   1. If there are AI-categorized rows queued for sign-off → open the
  //      mega-approve modal (skips the intermediate table view entirely).
  //   2. Else if there's a "Let's review" primary action → run it (filters
  //      the table to flagged rows + kicks off the inquiry chat).
  // Fires ONCE per component mount so re-renders don't re-open the modal.
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (!autoTrigger || autoFiredRef.current || !data) return;
    const aiReady = data?.progress?.mega_ready_rows || 0;
    if (aiReady > 0) {
      autoFiredRef.current = true;
      openMega();
    } else if (primary) {
      autoFiredRef.current = true;
      onApplyAction?.(primary);
    }
  }, [autoTrigger, data, primary, onApplyAction]);

  // Inline mode (dedicated /accounting/review-report page): auto-open the
  // bucket report as soon as the copilot has scanned the books, and
  // re-open whenever the book changes (unlike the modal, we don't want
  // the report to close permanently after one dry-run).
  const inlineOpenRef = useRef(false);
  useEffect(() => {
    if (!inline || !data || headerOnly) return;
    if (megaPreview) return;      // already open
    if (inlineOpenRef.current) return;
    inlineOpenRef.current = true;
    openMega();
  }, [inline, data, megaPreview, headerOnly]);
  useEffect(() => {
    // Reset the guard when the current company changes so re-selecting a
    // company on the report page re-fires the dry-run.
    inlineOpenRef.current = false;
  }, [currentId]);
  // Derived state for the mega-approve modal (kept out of state so it stays
  // in sync with megaSelected without an effect).
  //
  // Bucket list filter — only the SEARCH text narrows the main row list
  // (typing "amazon" hides Walmart / Dollar General / etc). Withdrawal,
  // Deposit, and date-range are sub-row-only filters — they narrow the
  // txns inside an expanded bucket but never remove main rows, because
  // the bucket preview payload doesn't carry per-txn dates and its
  // aggregate amount is stored as an absolute value.
  const megaVendors = megaPreview?.vendors || [];
  const _qMain = (megaSearch || "").trim().toLowerCase();
  const filteredVendors = _qMain
    ? megaVendors.filter(v => (v.contact_name || "").toLowerCase().includes(_qMain))
    : megaVendors;

  // When view mode = "category", cluster filteredVendors into groups keyed by
  // their effective account (user override wins over AI pick). Each group
  // header shows the account name + how many buckets/rows sit under it, so
  // the user can eyeball outliers ("why does Owner's Draw have 3 vendors?").
  // We do it here — not in a memo — because filteredVendors is already
  // recomputed on every render (cheap enough at ~200 vendors max).
  const megaGroups = (() => {
    if (megaViewMode !== "category" && megaViewMode !== "stepper") return null;
    const map = new Map();
    for (const v of filteredVendors) {
      const overrideId = megaOverrides[v.key];
      const acct = overrideId
        ? accounts.find(a => a.id === overrideId)
        : accounts.find(a => a.id === v.account?.id)
          || accounts.find(a => String(a.code) === String(v.account?.code))
          || v.account;
      const gkey = acct?.id || acct?.code || "__none__";
      if (!map.has(gkey)) {
        map.set(gkey, { account: acct, vendors: [], totalRows: 0, totalAmount: 0 });
      }
      const g = map.get(gkey);
      g.vendors.push(v);
      g.totalRows += v.count;
      g.totalAmount += v.amount;
    }
    // Sort groups by total rows desc (biggest categories first).
    return Array.from(map.values()).sort((a, b) => b.totalRows - a.totalRows);
  })();
  const selectedRows = megaVendors.reduce(
    (s, v) => s + (megaSelected.has(v.key) ? v.count : 0), 0
  );
  const selectedAmount = megaVendors.reduce(
    (s, v) => s + (megaSelected.has(v.key) ? v.amount : 0), 0
  );
  const selectedBuckets = megaVendors.reduce(
    (s, v) => s + (megaSelected.has(v.key) ? 1 : 0), 0
  );

  // Extracted so the mega-approve UI can render either as a fixed-
  // position modal (default) or inline as a page section (when the
  // parent passes `inline`).
  const renderMegaBody = () => (
    <>
            {megaPreview.total_rows === 0 ? (
              <>
                <div className="text-base font-semibold text-slate-900 mb-1">Nothing to approve</div>
                <div className="text-sm text-slate-600 mb-4">No AI-categorized-unreviewed rows found for vendors with a unanimous AI opinion. You're clean.</div>
                <NextStepCard currentId={currentId} inline={inline} onClose={() => setMegaPreview(null)} />
                {!inline && (
                  <button onClick={() => setMegaPreview(null)}
                          className="mt-3 w-full py-2 rounded-md bg-slate-900 text-white text-sm font-medium">Close</button>
                )}
              </>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="text-base font-semibold text-slate-900">
                    Approve <span data-testid="mega-selected-rows">{selectedRows.toLocaleString()}</span> rows across <span data-testid="mega-selected-vendors">{selectedBuckets}</span> {selectedBuckets === 1 ? "bucket" : "buckets"}?
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* View-mode toggle: order-by-rows vs. group-by-category.
                        Tiny paired icon-buttons that visually echo the
                        "How To" button on the right so the header row
                        doesn't feel unbalanced. */}
                    <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden">
                      <button
                        data-testid="mega-view-rows"
                        onClick={() => setMegaViewMode("rows")}
                        title="Sort by row count (most rows first)"
                        className={`p-1.5 transition ${
                          megaViewMode === "rows"
                            ? "bg-slate-900 text-white"
                            : "text-slate-500 hover:bg-slate-50"
                        }`}
                      >
                        <ListOrdered size={14} />
                      </button>
                      <button
                        data-testid="mega-view-category"
                        onClick={() => setMegaViewMode("category")}
                        title="Group buckets by category"
                        className={`p-1.5 transition border-l border-slate-200 ${
                          megaViewMode === "category"
                            ? "bg-slate-900 text-white"
                            : "text-slate-500 hover:bg-slate-50"
                        }`}
                      >
                        <LayoutList size={14} />
                      </button>
                      <button
                        data-testid="mega-view-stepper"
                        onClick={() => { setMegaViewMode("stepper"); setFocusedGroupIdx(0); }}
                        title="Review one category at a time (stepper)"
                        className={`p-1.5 transition border-l border-slate-200 ${
                          megaViewMode === "stepper"
                            ? "bg-slate-900 text-white"
                            : "text-slate-500 hover:bg-slate-50"
                        }`}
                      >
                        <Focus size={14} />
                      </button>
                    </div>
                    {howToRunning ? (
                      <button
                        data-testid="mega-howto-cancel"
                        onClick={cancelHowTo}
                        className="text-xs px-2 py-1 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                      >
                        Stop tour
                      </button>
                    ) : (
                      <button
                        data-testid="mega-howto"
                        onClick={runHowTo}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100"
                        title="Have the AI walk you through this screen"
                      >
                        <Sparkles size={12} /> How To
                      </button>
                    )}
                  </div>
                </div>
                <div
                  className="mb-2 flex flex-wrap items-center gap-2"
                  data-testid="mega-subrow-filters"
                >
                  <div className="relative flex-1 min-w-[240px]">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="mega-filter-search"
                      type="text"
                      value={megaSearch}
                      onChange={(e) => setMegaSearch(e.target.value)}
                      placeholder="Search merchant, description, or contact…"
                      className="w-full pl-8 pr-2.5 py-1.5 rounded border border-slate-300 text-xs bg-white"
                    />
                  </div>
                  <div className="inline-flex items-center gap-1 border rounded-md bg-white px-2 py-1">
                    <Calendar size={13} className="text-slate-400" />
                    <input
                      data-testid="mega-filter-date-from"
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="text-xs bg-transparent focus:outline-none font-mono-num text-slate-700"
                      aria-label="From date"
                    />
                    <span className="text-slate-400 text-xs">–</span>
                    <input
                      data-testid="mega-filter-date-to"
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="text-xs bg-transparent focus:outline-none font-mono-num text-slate-700"
                      aria-label="To date"
                    />
                  </div>
                  <button
                    data-testid="mega-filter-withdrawal"
                    onClick={() => setFilterDirection((d) => d === "out" ? "" : "out")}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border ${
                      filterDirection === "out"
                        ? "border-rose-600 bg-rose-600 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                    title="Show only withdrawals (money out)"
                  >
                    Withdrawal
                  </button>
                  <button
                    data-testid="mega-filter-deposit"
                    onClick={() => setFilterDirection((d) => d === "in" ? "" : "in")}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border ${
                      filterDirection === "in"
                        ? "border-emerald-600 bg-emerald-600 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                    title="Show only deposits (money in)"
                  >
                    Deposit
                  </button>
                  {(megaSearch || filterDateFrom || filterDateTo || filterDirection) && (
                    <button
                      data-testid="mega-filter-clear"
                      onClick={() => {
                        setMegaSearch("");
                        setFilterDateFrom("");
                        setFilterDateTo("");
                        setFilterDirection("");
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-slate-900 border border-transparent hover:border-slate-200 rounded"
                    >
                      <X size={12} /> Clear filters
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1.5">
                  <span>Click a row to include/exclude · change the category pill to override</span>
                  <div className="flex gap-2">
                    <button data-testid="mega-select-all"
                            className="text-emerald-700 hover:underline"
                            onClick={() => setMegaSelected(new Set(megaPreview.vendors.map(v => v.key)))}>
                      Select all
                    </button>
                    <button data-testid="mega-select-none"
                            className="text-slate-600 hover:underline"
                            onClick={() => setMegaSelected(new Set())}>
                      None
                    </button>
                  </div>
                </div>
                <div className="space-y-1 mb-3 flex-1 min-h-[240px] overflow-y-auto pr-1" data-testid="mega-vendor-list">
                  {(() => {
                    // Row renderer — closes over accounts, megaSelected, etc.
                    // Same JSX regardless of view mode, just interpose group
                    // headers when megaViewMode === "category".
                    const renderRow = (c) => {
                    const on = megaSelected.has(c.key);
                    // Effective account = user override (if any) else AI's pick.
                    const overrideId = megaOverrides[c.key];
                    const effAccount = overrideId
                      ? accounts.find(a => a.id === overrideId)
                      : accounts.find(a => a.id === c.account?.id)
                        || accounts.find(a => String(a.code) === String(c.account?.code))
                        || c.account;
                    const isOverridden = !!overrideId;
                    // How-To tour: apply the rainbow-shimmer highlight to
                    // the specific sub-element being described. `hi(step)`
                    // returns the shimmer class ONLY when we're on that
                    // step AND this row is the demo-target. The row itself
                    // stays visually white for the entire tour so the
                    // sub-element highlight pops without competing green.
                    const isTourRow = howToTargetKey === c.key;
                    const isTourActive = isTourRow && howToStep !== null && howToStep < 7;
                    const hi = (step) => (isTourRow && howToStep === step ? "ai-shimmer-btn bg-white" : "");
                    // Non-target rows get muted while the tour is running so
                    // the target row visibly pops. Empty when not in tour.
                    const tourDimClass = (howToRunning && !isTourRow) ? "opacity-40" : "";
                    return (
                      <React.Fragment key={c.key}>
                      <div
                        data-testid={`mega-vendor-${c.key}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleExpandBucket(c)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleExpandBucket(c); } }}
                        title={expandedBucket === c.key ? "Hide sub-transactions" : "Show sub-transactions"}
                        className={`w-full flex md:grid md:grid-cols-[auto_240px_minmax(200px,1fr)_auto_auto_auto] items-center gap-2.5 rounded border px-3 py-2 text-sm transition-all cursor-pointer ${tourDimClass} ${
                          isTourRow && howToStep === 0
                            ? "ai-shimmer-btn shadow-[0_0_0_4px_rgba(6,182,212,0.15)]"
                            : isTourActive
                              ? "bg-white border-cyan-300 shadow-[0_0_0_4px_rgba(6,182,212,0.15),0_8px_24px_-8px_rgba(6,182,212,0.35)]"
                              : focus?.bucket && focus.key === c.key
                                ? "ai-shimmer-btn"
                                : on
                                  ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                                  : "border-slate-200 bg-slate-50 opacity-60 hover:opacity-100 hover:bg-slate-100"
                        }`}
                      >
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleVendor(c.key); }}
                          className={`w-4 h-4 rounded flex items-center justify-center text-white text-[10px] shrink-0 ${on ? "bg-emerald-600" : "bg-slate-300"} ${hi(1)}`}
                          title={on ? "Exclude from batch" : "Include in batch"}
                        >
                          {on ? "✓" : ""}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpandBucket(c); }}
                          className="min-w-0 shrink-0 text-left md:w-full md:shrink"
                          title={expandedBucket === c.key ? "Hide sub-transactions" : "Show sub-transactions"}
                        >
                          <span className="font-semibold text-slate-900 truncate max-w-[220px] md:max-w-full inline-block md:block align-middle">{c.contact_name}</span>
                        </button>
                        <div
                          className={`min-w-0 flex-1 flex items-center gap-1 rounded ${hi(2)}`}
                          onClick={(e) => {
                            // Only swallow the click when the user actually hit a
                            // control (the picker button or the info tooltip).
                            // Clicks in the empty flex-fill space to the right of
                            // the picker should bubble up so the whole row
                            // still toggles expand/collapse.
                            if (e.target.closest('button, input, select, textarea, [role="combobox"], [role="listbox"], [role="option"]')) {
                              e.stopPropagation();
                            }
                          }}
                        >
                          <AccountPicker
                            testId={`mega-vendor-cat-${c.key}`}
                            value={effAccount?.id || ""}
                            accounts={accounts}
                            companyId={currentId}
                            isOverridden={isOverridden}
                            onChange={(newId) => {
                              setMegaOverrides(prev => {
                                const next = { ...prev };
                                if (newId === c.account?.id) {
                                  delete next[c.key];
                                } else if (newId) {
                                  next[c.key] = newId;
                                }
                                return next;
                              });
                              // If a brand-new account was just created,
                              // reload the accounts list so the label
                              // renders correctly.
                              if (!accounts.find(a => a.id === newId)) {
                                window.dispatchEvent(new CustomEvent("axiom:action",
                                  { detail: { kind: "accounts:changed", at: Date.now() } }));
                              }
                            }}
                          />
                          <span data-testid={`mega-vendor-info-${c.key}`} className={`rounded ${hi(3)}`}>
                            <AccountInfoTooltip account={effAccount} />
                          </span>
                        </div>
                        <div className={`text-right shrink-0 rounded px-1 ${hi(4)}`}>
                          <div className="font-mono-num text-slate-900 text-sm flex items-center justify-end gap-1.5">
                            <span
                              data-testid={`mega-vendor-expand-${c.key}`}
                              className="inline-flex items-center gap-0.5 text-slate-600"
                              title={expandedBucket === c.key ? "Click the row to hide" : "Click the row to show these transactions"}
                            >
                              {c.count} rows
                              <ChevronDown
                                size={12}
                                className={`transition-transform ${expandedBucket === c.key ? "rotate-180" : ""}`}
                              />
                            </span>
                            {c.flagged_count > 0 && (
                              <span
                                data-testid={`mega-vendor-flagged-${c.key}`}
                                title={`${c.flagged_count} of these row${c.flagged_count === 1 ? " is" : "s are"} flagged for review — approving this bucket clears them alongside the confident rows.`}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold font-mono-num"
                              >
                                ⚠ {c.flagged_count} flagged
                              </span>
                            )}
                          </div>
                          <div className="font-mono-num text-slate-500 text-xs">${c.amount.toLocaleString("en-US", {maximumFractionDigits: 0})}</div>
                        </div>
                        <button
                          data-testid={`mega-vendor-ai-${c.key}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            emitAction("ai-open");
                            emitAction("ai-tell-me-about-bucket", {
                              bucket: {
                                key: c.key,
                                contact_name: c.contact_name,
                                count: c.count,
                                amount: c.amount,
                                account_code: effAccount?.code,
                                account_name: effAccount?.name,
                                account_type: effAccount?.type,
                                is_overridden: isOverridden,
                                ai_original_code: c.account?.code,
                                ai_original_name: c.account?.name,
                              },
                            });
                          }}
                          className={`ml-1 p-1 rounded hover:bg-fuchsia-100 text-fuchsia-600 shrink-0 ${hi(5)}`}
                          title={`Ask AI about the ${c.contact_name} bucket`}
                        >
                          <Sparkles size={14} />
                        </button>
                        <button
                          data-testid={`mega-vendor-approve-${c.key}`}
                          onClick={(e) => { e.stopPropagation(); approveOne(c); }}
                          className={`ml-1 shrink-0 text-emerald-700 hover:text-emerald-900 text-sm font-semibold hover:underline rounded px-1 ${hi(6)}`}
                          title={`Approve ${c.count} rows now${isOverridden ? " (with override)" : ""}`}
                        >
                          Approve →
                        </button>
                      </div>
                      {expandedBucket === c.key && (
                        <BucketExpansion
                          bucketKey={c.key}
                          currentId={currentId}
                          data={expandedRows[c.key]}
                          accounts={accounts}
                          contacts={contactsList}
                          onUpdate={(txnId, patch) => updateBucketRow(c.key, txnId, patch)}
                          onBulkUpdate={bulkUpdateBucketRows}
                          onRefresh={() => refreshBucket(c)}
                          filterSearch={megaSearch}
                          filterDateFrom={filterDateFrom}
                          filterDateTo={filterDateTo}
                          filterDirection={filterDirection}
                        />
                      )}
                    </React.Fragment>
                    );
                    };
                    if (filteredVendors.length === 0) {
                      return (
                        <div
                          className="text-[11px] text-slate-500 text-center py-4"
                          data-testid="mega-vendor-list-empty"
                        >
                          {_qMain
                            ? `No buckets match "${megaSearch}". Clear search to see all ${megaVendors.length}.`
                            : "No AI-categorized vendors to review."}
                        </div>
                      );
                    }
                    if (megaViewMode === "stepper" && megaGroups) {
                      // Stepper: show ONE group + info card + prev/next nav.
                      // Clamp the index in case the list shrank underneath us.
                      const idx = Math.min(focusedGroupIdx, megaGroups.length - 1);
                      const g = megaGroups[idx];
                      if (!g) {
                        return (
                          <div className="text-center py-10 text-slate-500 text-sm" data-testid="stepper-done">
                            <div className="text-emerald-700 font-semibold text-base mb-1">All groups reviewed 🎉</div>
                            Nothing left to approve in this cycle.
                          </div>
                        );
                      }
                      return (
                        <div data-testid={`mega-stepper-${g.account?.code || "none"}`}>
                          {/* Info card was moved to the page header (top-right,
                              next to the "AI Cleanup Review" title). Only the
                              navigation and rows render here. */}
                          <div className="flex items-center gap-2 mb-3">
                            <button
                              data-testid={`stepper-prev`}
                              onClick={gotoPrev}
                              disabled={idx === 0}
                              className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                            >
                              ← Previous
                            </button>
                            <button
                              data-testid={`stepper-next`}
                              onClick={gotoNext}
                              disabled={idx >= megaGroups.length - 1}
                              className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                            >
                              Skip to next →
                            </button>
                            <button
                              data-testid={`stepper-approve-group`}
                              onClick={() => approveGroup(g)}
                              className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-xs font-semibold"
                            >
                              Approve group ({g.vendors.length}) →
                            </button>
                          </div>
                          <div className="space-y-1">
                            {g.vendors.map(renderRow)}
                          </div>
                          {/* Duplicate the nav at the bottom so the CPA
                              doesn't have to scroll back up after
                              inspecting a long bucket list. */}
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                            <button
                              data-testid="stepper-prev-bottom"
                              onClick={gotoPrev}
                              disabled={idx === 0}
                              className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                            >
                              ← Previous
                            </button>
                            <button
                              data-testid="stepper-next-bottom"
                              onClick={gotoNext}
                              disabled={idx >= megaGroups.length - 1}
                              className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                            >
                              Skip to next →
                            </button>
                            <button
                              data-testid="stepper-approve-group-bottom"
                              onClick={() => approveGroup(g)}
                              className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-xs font-semibold"
                            >
                              Approve group ({g.vendors.length}) →
                            </button>
                          </div>
                        </div>
                      );
                    }
                    if (megaViewMode === "category" && megaGroups) {
                      return megaGroups.map(g => (
                        <div key={g.account?.id || g.account?.code || "__none__"} className="mb-2">
                          <div
                            className="sticky top-0 z-10 bg-slate-100 border border-slate-200 rounded-t px-3 py-1.5 flex items-center gap-2 text-[11px]"
                            data-testid={`mega-group-${g.account?.code || "none"}`}
                          >
                            <span className="font-semibold text-slate-800">
                              {g.account?.code ? `${g.account.code} · ` : ""}{g.account?.name || "Uncategorized"}
                            </span>
                            <span className="text-slate-500 tabular-nums">
                              {g.totalRows.toLocaleString()} rows · {g.vendors.length} {g.vendors.length === 1 ? "bucket" : "buckets"} · ${Math.round(g.totalAmount).toLocaleString()}
                            </span>
                            <button
                              data-testid={`mega-group-approve-${g.account?.code || "none"}`}
                              onClick={() => approveGroup(g)}
                              className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-[11px] font-semibold transition"
                              title={`Approve all ${g.vendors.length} ${g.vendors.length === 1 ? "bucket" : "buckets"} (${g.totalRows} rows) under ${g.account?.name || "this category"}`}
                            >
                              Approve group →
                            </button>
                          </div>
                          <div className="space-y-1">
                            {g.vendors.map(renderRow)}
                          </div>
                        </div>
                      ));
                    }
                    return filteredVendors.map(renderRow);
                  })()}
                </div>
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3">
                  ⚠ You&apos;ll have 60 seconds to Undo after applying. Undo restores each row&apos;s original category too.
                </div>
                <label className="flex items-center gap-2 mb-3 text-[11px] text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="mega-auto-create-rules"
                    checked={autoCreateRules}
                    onChange={(e) => setAutoCreateRules(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span>
                    <span className="font-medium">Auto-create rules from approved buckets</span>
                    <span className="text-slate-500"> — so future {`{merchant}`} imports land on the same account automatically. Skips payment apps (Venmo / Zelle / Cash App) and ambiguous vendors. Undo removes any created rules too.</span>
                  </span>
                </label>
                <div className="flex gap-2">
                  {!inline && (
                    <button
                      data-testid="mega-approve-cancel"
                      onClick={() => setMegaPreview(null)}
                      disabled={megaBusy}
                      className="flex-1 py-2 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    data-testid="mega-approve-confirm"
                    onClick={applyMega}
                    disabled={megaBusy || megaSelected.size === 0}
                    className={`flex-1 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 ${howToStep === 7 ? "ai-shimmer-btn" : ""}`}
                  >
                    {megaBusy ? "Approving…" : `Approve ${selectedRows.toLocaleString()} rows`}
                  </button>
                </div>
              </>
            )}
    </>
  );

  return (
    <>
    <div data-testid="cleanup-copilot" className="rounded-xl border border-slate-200 bg-gradient-to-br from-indigo-50/40 via-white to-fuchsia-50/40 shadow-sm p-4">
      <div className="flex items-center gap-4 flex-wrap">
        <Donut progress={data?.progress} />
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <Sparkles size={12} className="text-fuchsia-500" /> AI Cleanup Copilot
          </div>
          <div className="mt-1 text-sm text-slate-800 leading-snug">
            {busy && !data ? (
              <span className="inline-flex items-center gap-2 text-slate-500">
                <Loader2 size={13} className="animate-spin" /> Scanning your books…
              </span>
            ) : (
              pitchFor(primary, data?.progress)
            )}
          </div>
          {data?.progress?.total ? (
            <div className="mt-1 text-[11px] text-slate-500">
              {data.progress.reviewed} reviewed · {data.progress.ai_categorized} AI-categorized · {data.progress.uncategorized} uncategorized · {data.progress.flagged} flagged
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {activeStep && (
            forceStep ? (
              // On a dedicated step page (e.g. AI Cleanup Review = Step 1)
              // the card is a static "you are here" marker — blue outline,
              // no shimmer, no click. Prev / next chevrons let the CPA
              // jump straight to the neighbouring step page inline.
              <div
                data-testid="cleanup-active-step-badge"
                className="hidden md:flex items-center gap-4 w-[400px] max-w-[42vw] rounded-xl border-2 border-blue-500 bg-white px-4 py-3 shadow-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <div className="font-heading font-semibold text-slate-900 text-[15px] leading-tight truncate flex-1 min-w-0">
                      Step {activeStep.display}: {activeStep.title}
                    </div>
                    {(() => {
                      const nav = STEP_NAV[String(activeStep.display)];
                      if (!nav) return null;
                      return (
                        <div className="shrink-0 inline-flex items-center gap-0.5">
                          {nav.prev && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(nav.prev.href); }}
                              title={`Go to ${nav.prev.label}`}
                              aria-label={`Go to ${nav.prev.label}`}
                              data-testid="cleanup-step-nav-prev"
                              className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                            >
                              <ChevronLeft size={16} />
                            </button>
                          )}
                          {nav.next && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(nav.next.href); }}
                              title={`Go to ${nav.next.label}`}
                              aria-label={`Go to ${nav.next.label}`}
                              data-testid="cleanup-step-nav-next"
                              className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                            >
                              <ChevronRightIcon size={16} />
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {(activeStep.subtitle_parts || activeStep.subtitle) && (
                    <div className="mt-1 text-[12px] text-slate-500 leading-snug line-clamp-2">
                      <StepSubtitle step={activeStep} onNavigate={navigate} />
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex flex-col items-end justify-center pl-3 border-l border-slate-200">
                  <div className="font-heading text-3xl font-bold text-slate-900 leading-none tabular-nums" data-testid="copilot-step-headline">
                    {activeStep.eta ? activeStep.eta.value : activeStep.count.toLocaleString()}
                  </div>
                  <div className="mt-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">
                    {activeStep.eta ? activeStep.eta.unit : activeStep.unit}
                  </div>
                  {activeStep.eta && (
                    <div className="mt-0.5 text-[10px] text-slate-400 tabular-nums whitespace-nowrap">
                      {activeStep.count.toLocaleString()} {activeStep.unit}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // On the main Transactions page, the card is a clickable link
              // to the next-open step's page with the rainbow shimmer to
              // signal "AI action available — pick this up."
              // When the user has flipped todos into chat-review mode
              // (localStorage `dashboard-todos-mode` = "chat"), route
              // to the chat-review surface instead — Step 3B → the
              // no-contact chat tab, Step 3C → the checks chat tab,
              // everything else → the "No Category" chat tab (default
              // entry point).
              (() => {
                const chatCta = (() => {
                  const key = String(activeStep.display || activeStep.n);
                  if (key === "3B") return "/accounting/review-chat?tab=transactions";
                  if (key === "3C") return "/accounting/review-chat?tab=checks";
                  return "/accounting/review-chat?tab=no_category";
                })();
                const aiCta = String(activeStep.display || activeStep.n) === "1"
                  ? "/accounting/transactions?filter=unapproved&txnTour=1"
                  : "/accounting/review-chat?tab=checks";
                const targetHref = chatModeOn ? chatCta : aiModeOn ? aiCta : activeStep.cta_link;
                // Chat-mode overrides — when the CPA has flipped todos
                // into chat-review, show the live chat-queue totals
                // ("X questions · Y checks") and a dollar-value progress
                // ring instead of the checklist-mode step count.
                const useChat = !!(chatModeOn && chatQueue);
                const noCatN = useChat ? (chatQueue.no_category?.length || 0) : 0;
                const txnN   = useChat ? (chatQueue.transactions?.length || 0) : 0;
                const checkN = useChat ? (chatQueue.checks?.length || 0) : 0;
                const qCount = noCatN + txnN;
                const pct    = useChat ? Math.max(0, Math.min(100, Number(chatQueue.progress?.pct_confirmed || 0))) : 0;
                const title  = useChat ? "Review questions & checks" : `Step ${activeStep.display}: ${activeStep.title}`;
                const tipLabel = useChat
                  ? `Go to chat review — ${qCount} questions, ${checkN} checks (${pct}% by $ done)`
                  : (chatModeOn
                      ? `Go to chat review — ${activeStep.title}`
                      : `Go to Step ${activeStep.display} — ${activeStep.title}`);
                // Small SVG donut for the dollar-value ring; 34px so it
                // fits alongside the count in the same right rail slot.
                const R = 15, C = 2 * Math.PI * R;
                const dash = (pct / 100) * C;
                return (
              <button
                type="button"
                data-testid="cleanup-active-step-badge"
                onClick={() => targetHref && navigate(targetHref)}
                title={tipLabel}
                className="ai-shimmer-bubble hidden md:flex items-center gap-4 w-[400px] max-w-[42vw] rounded-xl px-4 py-3 shadow-sm text-left cursor-pointer transition-shadow hover:shadow-md"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-slate-900 text-[15px] leading-tight truncate">
                    {title}
                  </div>
                  {useChat ? (
                    <div className="mt-1 text-[12px] text-slate-500 leading-snug">
                      <span className="font-mono-num font-semibold text-slate-700">{qCount}</span> questions ·{" "}
                      <span className="font-mono-num font-semibold text-slate-700">{checkN}</span> checks
                    </div>
                  ) : (
                    (activeStep.subtitle_parts || activeStep.subtitle) && (
                      <div className="mt-1 text-[12px] text-slate-500 leading-snug line-clamp-2">
                        <StepSubtitle step={activeStep} onNavigate={navigate} />
                      </div>
                    )
                  )}
                </div>
                <div className="shrink-0 flex flex-col items-end justify-center pl-3 border-l border-slate-200">
                  {useChat ? (
                    <>
                      <div className="relative h-9 w-9">
                        <svg viewBox="0 0 34 34" className="h-9 w-9 -rotate-90">
                          <circle cx="17" cy="17" r={R} fill="none" stroke="#e2e8f0" strokeWidth="3" />
                          <circle
                            cx="17" cy="17" r={R} fill="none"
                            stroke={pct >= 66 ? "#059669" : pct >= 33 ? "#d97706" : "#e11d48"}
                            strokeWidth="3" strokeLinecap="round"
                            strokeDasharray={`${dash} ${C - dash}`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center font-mono-num text-[10px] font-bold text-slate-800">
                          {pct}%
                        </div>
                      </div>
                      <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">
                        by $ done
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-heading text-3xl font-bold text-slate-900 leading-none tabular-nums">
                        {activeStep.eta ? activeStep.eta.value : activeStep.count.toLocaleString()}
                      </div>
                      <div className="mt-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">
                        {activeStep.eta ? activeStep.eta.unit : activeStep.unit}
                      </div>
                      {activeStep.eta && (
                        <div className="mt-0.5 text-[10px] text-slate-400 tabular-nums whitespace-nowrap">
                          {activeStep.count.toLocaleString()} {activeStep.unit}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </button>
                );
              })()
            )
          )}
          {(() => {
            // The three action buttons (Let's review / Approve AI
            // Categorized / Individual Review) were retired at the
            // user's request — the CPA reaches those flows via the
            // dashboard checklist tiles now. Keep the computed data
            // (mega-ready count, primary action) available for other
            // components that read `data.progress`.
            return null;
          })()}
        </div>
      </div>

      {rest.length > 0 && !inline && !hideChips && (
        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          {rest.map((a, i) => {
            const style = KIND_STYLES[a.kind] || KIND_STYLES.flagged_batch;
            return (
              <button
                key={i}
                data-testid={`cleanup-chip-${a.kind}-${a.contact_id || i}`}
                onClick={() => onApplyAction?.(a)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full border ${style.chipCls}`}
                title={a.why}
              >
                <span>{style.dot}</span>
                <span>{a.label}</span>
                {"count" in a && (
                  <span className="ml-0.5 px-1 rounded bg-white/60 font-mono-num">{a.count}</span>
                )}
              </button>
            );
          })}
          {total > 0 && actions.length < 3 && (
            <span className="text-[11px] text-slate-500">Nothing else urgent — you're in good shape.</span>
          )}
        </div>
      )}
    </div>
    {megaPreview && !headerOnly && (
      inline ? (
        // Inline rendering: two-column header (title left, group info
        // card right) followed by the report body. The group info card
        // is only meaningful in stepper mode — otherwise it's suppressed
        // so the header collapses to a single-column title.
        (() => {
          let currentGroup = null;
          if (megaViewMode === "stepper" && megaGroups && megaGroups.length > 0) {
            const idx = Math.min(focusedGroupIdx, megaGroups.length - 1);
            currentGroup = megaGroups[idx];
          }
          return (
            <>
              {(inlineTitle || currentGroup) && (
                <div className="mt-6 mb-3 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {inlineTitle && (
                      <h1 className="text-3xl font-heading font-bold text-slate-900 leading-tight">
                        {inlineTitle}
                      </h1>
                    )}
                    {inlineSubtitle && (
                      <div className="text-sm text-slate-500 mt-1">
                        {inlineSubtitle}
                      </div>
                    )}
                  </div>
                  {currentGroup && (
                    <div
                      className="w-[420px] shrink-0 rounded-lg bg-white border border-cyan-400 ring-1 ring-cyan-100 shadow-sm px-4 py-3"
                      data-testid="stepper-info-card"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                          Group {Math.min(focusedGroupIdx, megaGroups.length - 1) + 1} of {megaGroups.length}
                        </span>
                        <span className="text-[10px] text-slate-500 tabular-nums">
                          {currentGroup.totalRows.toLocaleString()} rows · {currentGroup.vendors.length} {currentGroup.vendors.length === 1 ? "bucket" : "buckets"} · ${Math.round(currentGroup.totalAmount).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-0.5 font-heading font-semibold text-base text-slate-900 truncate">
                        {currentGroup.account?.code ? `${currentGroup.account.code} · ` : ""}{currentGroup.account?.name || "Uncategorized"}
                      </div>
                      {(() => {
                        const def = currentGroup.account ? accountDefinition(currentGroup.account) : null;
                        return def ? (
                          <div className="text-[11px] text-slate-600 mt-1 leading-snug line-clamp-3">
                            {def}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              )}
              {reportHeader}
              <div className="mt-4" data-testid="mega-approve-inline">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col p-5">
                  {renderMegaBody()}
                </div>
              </div>
            </>
          );
        })()
      ) : (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center p-4"
             data-testid="mega-approve-modal"
             onClick={() => !megaBusy && setMegaPreview(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col p-5"
               onClick={(e) => e.stopPropagation()}>
            {renderMegaBody()}
          </div>
        </div>
      )
    )}
      {megaUndo && (
        <div className="fixed bottom-6 right-6 z-[70] max-w-sm bg-slate-900 text-white rounded-lg shadow-2xl px-4 py-3 flex items-center gap-3"
             data-testid="mega-undo-toast">
          <div className="text-sm">
            <div className="font-semibold">
              Approved {megaUndo.count.toLocaleString()} rows
              {megaUndo.rules_created > 0 && (
                <span> · created {megaUndo.rules_created} rule{megaUndo.rules_created === 1 ? "" : "s"}</span>
              )}
            </div>
            <div className="text-slate-300 text-xs">You have 60 seconds to undo (rows + rules).</div>
          </div>
          <button
            data-testid="mega-undo-btn"
            onClick={undoMega}
            className="px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-xs font-semibold"
          >
            Undo
          </button>
          <button
            onClick={() => setMegaUndo(null)}
            className="text-slate-400 hover:text-white text-lg leading-none"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}


// Expansion panel: shows the actual transactions inside a bucket with
// per-row Contact + Category pickers. Rendered as a sibling row right
// under the clicked bucket header. Uses the passed-in `accounts` and
// `contacts` arrays (already loaded once at parent level) so opening a
// bucket is instant after the first fetch. The bulk toolbar mirrors the
// Transactions page (Approve all / Reclassify / Change contact / Make
// these rules) so users don't relearn a second interaction pattern —
// all four actions are scoped to the checked sub-rows only.
function BucketExpansion({ bucketKey, currentId, data, accounts, contacts, onUpdate, onBulkUpdate, onRefresh, filterSearch = "", filterDateFrom = "", filterDateTo = "", filterDirection = "" }) {
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [applying, setApplying] = React.useState(false);
  const [reclassOpen, setReclassOpen] = React.useState(false);
  const [contactPickerOpen, setContactPickerOpen] = React.useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = React.useState(false);
  const [ruleQueue, setRuleQueue] = React.useState(null);
  // Confirmation-modal state — same UX gate the Transactions page uses so
  // bulk writes require an explicit second tap.
  const [pendingConfirm, setPendingConfirm] = React.useState(null);
  // Reset selection whenever the bucket is re-opened with new rows.
  const rowIdsKey = (data?.rows || []).map(r => r.id).join(",");
  React.useEffect(() => { setSelectedIds(new Set()); }, [rowIdsKey]);
  if (!data) return null;
  if (data.loading) {
    return (
      <div className="w-full pl-8 pr-3 py-2 text-xs text-slate-500 bg-slate-50/50 rounded-b border-x border-b border-slate-200 -mt-1">
        Loading transactions…
      </div>
    );
  }
  if (data.err) {
    return (
      <div className="w-full pl-8 pr-3 py-2 text-xs text-red-600 bg-red-50 rounded-b border-x border-b border-red-200 -mt-1">
        {data.err}
      </div>
    );
  }
  const allRows = data.rows || [];
  // Apply client-side filters that mirror the Transactions filter bar —
  // search text against merchant/description/contact, inclusive date
  // range against `date` (ISO YYYY-MM-DD), and Withdrawal/Deposit sign
  // filter on `amount`. Server always sends every row in the bucket;
  // this layer is purely cosmetic so the CPA can zoom in without
  // re-firing the network round-trip.
  const q = (filterSearch || "").trim().toLowerCase();
  const rows = allRows.filter(r => {
    if (q) {
      const hay = `${r.merchant || ""} ${r.description || ""} ${r.contact_name || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterDateFrom && (r.date || "") < filterDateFrom) return false;
    if (filterDateTo   && (r.date || "") > filterDateTo)   return false;
    if (filterDirection === "out" && !((r.amount ?? 0) < 0)) return false;
    if (filterDirection === "in"  && !((r.amount ?? 0) > 0)) return false;
    return true;
  });
  const filtersActive = !!(q || filterDateFrom || filterDateTo || filterDirection);
  if (!allRows.length) {
    return (
      <div className="w-full pl-8 pr-3 py-2 text-xs text-slate-500 bg-slate-50/50 rounded-b border-x border-b border-slate-200 -mt-1">
        No matching unreviewed transactions right now.
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div
        className="w-full pl-8 pr-3 py-3 text-xs text-slate-500 bg-slate-50/50 rounded-b border-x border-b border-slate-200 -mt-1 text-center"
        data-testid={`bucket-empty-filtered-${bucketKey}`}
      >
        No sub-transactions match the current filter. Clear filters to see all {allRows.length}.
      </div>
    );
  }
  const allSelected = selectedIds.size > 0 && selectedIds.size === rows.length;
  const toggleAll = () => {
    setSelectedIds(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  };
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const showToast = (message, type = "success") => {
    if (type === "error")      toast.error(message);
    else if (type === "info")  toast.info(message);
    else                       toast.success(message);
  };
  const bulkApprove = () => {
    if (!selectedIds.size) return;
    const count = selectedIds.size;
    setPendingConfirm({
      title: `Approve ${count} transaction${count === 1 ? "" : "s"}?`,
      body: "Marks the selected rows as human-reviewed. You can still edit individual rows afterwards.",
      confirmLabel: `Approve ${count}`,
      variant: "primary",
      exec: async () => {
        const ids = [...selectedIds];
        setApplying(true);
        try {
          await api.post(`/companies/${currentId}/transactions/bulk-approve`, ids);
          showToast(`Approved ${ids.length} transaction${ids.length === 1 ? "" : "s"}.`);
          setSelectedIds(new Set());
          onRefresh?.();
        } catch (e) {
          showToast(`Approve failed: ${e.response?.data?.detail || e.message}`, "error");
        } finally {
          setApplying(false);
        }
      },
    });
  };
  const bulkReclassifyApply = (categoryAccountId, contactId = null) => {
    if (!selectedIds.size) return;
    const acct = accounts.find(a => a.id === categoryAccountId);
    const contact = contactId ? contacts.find(c => c.id === contactId) : null;
    const count = selectedIds.size;
    setReclassOpen(false);
    setPendingConfirm({
      title: `Reclassify ${count} transaction${count === 1 ? "" : "s"}?`,
      body: `Category → ${acct?.name || "target"}`
            + (contact ? ` · Contact → ${contact.name}` : ""),
      confirmLabel: `Reclassify ${count}`,
      variant: "primary",
      exec: async () => {
        const ids = [...selectedIds];
        setApplying(true);
        try {
          const r = await api.post(`/companies/${currentId}/transactions/bulk-reclassify`, {
            transaction_ids: ids,
            category_account_id: categoryAccountId,
            contact_id: contactId || null,
          });
          showToast(`Reclassified ${r.data?.updated ?? ids.length} → ${acct?.name || "category"}`);
          setSelectedIds(new Set());
          onRefresh?.();
        } catch (e) {
          showToast(`Reclassify failed: ${e.response?.data?.detail || e.message}`, "error");
        } finally {
          setApplying(false);
        }
      },
    });
  };
  const bulkSetContactApply = (contactId) => {
    if (!selectedIds.size) return;
    const contact = contactId ? contacts.find(c => c.id === contactId) : null;
    const count = selectedIds.size;
    setContactPickerOpen(false);
    setPendingConfirm({
      title: `Set contact on ${count} transaction${count === 1 ? "" : "s"}?`,
      body: contact
        ? `Contact → ${contact.name}`
        : "This will clear the contact on all selected rows.",
      confirmLabel: contact ? `Set → ${contact.name}` : `Clear on ${count}`,
      variant: contact ? "primary" : "danger",
      exec: async () => {
        const ids = [...selectedIds];
        setApplying(true);
        try {
          const r = await api.post(`/companies/${currentId}/transactions/bulk-set-contact`, {
            transaction_ids: ids,
            contact_id: contactId || null,
          });
          const label = contactId
            ? (contacts.find(c => c.id === contactId)?.name || "contact")
            : "no contact";
          showToast(`Updated ${r.data?.updated ?? ids.length} → ${label}`);
          setSelectedIds(new Set());
          onRefresh?.();
        } catch (e) {
          showToast(`Set contact failed: ${e.response?.data?.detail || e.message}`, "error");
        } finally {
          setApplying(false);
        }
      },
    });
  };
  // Consolidated Bulk Update — same UX as the Transactions page so users
  // don't relearn a second grammar. Contact + Category + Approve all in
  // one popup, empty fields skipped, single confirm gate. Uses the same
  // three backend endpoints in the same order (categorize → approve).
  const bulkUpdateApply = (patch) => {
    if (!selectedIds.size) return;
    const acct = patch.category_account_id ? accounts.find(a => a.id === patch.category_account_id) : null;
    const contact = patch.contact_id ? contacts.find(c => c.id === patch.contact_id) : null;
    const count = selectedIds.size;
    // Snapshot the selected IDs so the "& Make rule" branch still knows
    // what to propose after `runUpdate` clears the selection.
    const idsSnapshot = [...selectedIds];
    setBulkUpdateOpen(false);
    const bodyLines = [];
    if (acct)          bodyLines.push(`Category → ${acct.name}`);
    if (contact)       bodyLines.push(`Contact → ${contact.name}`);
    if (patch.approve) bodyLines.push("Mark as human-reviewed");
    const runUpdate = async () => {
      const ids = [...selectedIds];
      setApplying(true);
      try {
        if (patch.category_account_id) {
          await api.post(`/companies/${currentId}/transactions/bulk-reclassify`, {
            transaction_ids: ids,
            category_account_id: patch.category_account_id,
            contact_id: patch.contact_id || null,
          });
        } else if (patch.contact_id) {
          await api.post(`/companies/${currentId}/transactions/bulk-set-contact`, {
            transaction_ids: ids,
            contact_id: patch.contact_id,
          });
        }
        if (patch.approve) {
          await api.post(`/companies/${currentId}/transactions/bulk-approve`, ids);
        }
        const parts = [];
        if (acct)          parts.push(`category → ${acct.name}`);
        if (contact)       parts.push(`contact → ${contact.name}`);
        if (patch.approve) parts.push("approved");
        showToast(`Updated ${ids.length} txn(s)${parts.length ? ` — ${parts.join(", ")}` : ""}`);
        setSelectedIds(new Set());
        onRefresh?.();
      } catch (e) {
        showToast(`Bulk update failed: ${e.response?.data?.detail || e.message}`, "error");
        throw e;
      } finally {
        setApplying(false);
      }
    };
    setPendingConfirm({
      title: `Bulk update ${count} transaction${count === 1 ? "" : "s"}?`,
      body: bodyLines.join(" · ") || "No changes selected.",
      confirmLabel: `Update ${count}`,
      variant: "primary",
      exec: runUpdate,
      confirmAndRuleLabel: `Update ${count} & Make rule`,
      execAndRule: async () => {
        try { await runUpdate(); }
        catch { return; }   // Update failed — don't try to build rules on top.
        try {
          const r = await api.post(`/companies/${currentId}/rules/suggest-from-txns`, {
            transaction_ids: idsSnapshot,
          });
          const proposals = r.data?.proposals || [];
          if (proposals.length === 0) {
            showToast("No new rules to propose — rows may already be covered.", "info");
            return;
          }
          const [cls, tg] = await Promise.all([
            api.get(`/companies/${currentId}/classes`).catch(() => ({ data: {} })),
            api.get(`/companies/${currentId}/tags`).catch(() => ({ data: {} })),
          ]);
          setRuleQueue({
            proposals,
            index: 0,
            classes: (cls.data?.classes || cls.data?.items || []).map(x => ({ id: x.id, name: x.name })),
            tags:    (tg.data?.tags    || tg.data?.items    || []).map(x => ({ id: x.id, name: x.name })),
          });
        } catch (e) {
          showToast(`Rule suggestion failed: ${e.response?.data?.detail || e.message}`, "error");
        }
      },
    });
  };
  const bulkCreateRules = async () => {
    if (!selectedIds.size) return;
    const ids = [...selectedIds];
    setApplying(true);
    try {
      const r = await api.post(`/companies/${currentId}/rules/suggest-from-txns`, {
        transaction_ids: ids,
      });
      let proposals = r.data?.proposals || [];
      const [cls, tg] = await Promise.all([
        api.get(`/companies/${currentId}/classes`).catch(() => ({ data: {} })),
        api.get(`/companies/${currentId}/tags`).catch(() => ({ data: {} })),
      ]);
      if (proposals.length === 0) {
        // Fallback proposal — see Transactions.jsx `bulkCreateRules`
        // for the rationale. Always open the CreateRule modal so the
        // CPA can hand-author even when the backend has nothing to
        // suggest (rows already covered by an existing rule, etc.).
        const seed = (data?.rows || []).find(t => selectedIds.has(t.id)) || {};
        const dup = r.data?.duplicates_skipped || 0;
        if (dup > 0) {
          showToast(
            `${dup} row${dup === 1 ? " is" : "s are"} already covered — opening a blank rule you can tweak.`,
            "info",
          );
        }
        const seedAmt = Number(seed.amount || 0);
        const dirHint = seedAmt < 0 ? "out" : seedAmt > 0 ? "in" : "both";
        proposals = [{
          match_field:  seed.contact_id ? "contact" : "merchant",
          match_value:  seed.contact_id || (seed.merchant || ""),
          match_value_display: seed.contact_name || seed.merchant || "",
          account_id:   seed.category_account_id || null,
          account_code: seed.category_account_code || "",
          account_name: seed.category_account_name || "",
          contact_id:   seed.contact_id || null,
          contact_name: seed.contact_name || null,
          class_id:     null,
          class_name:   null,
          tag_ids:      [],
          posting_mode: "auto",
          priority:     seed.contact_id ? 10 : 0,
          covered_txn_count: selectedIds.size,
          direction_hint:    dirHint,
        }];
      }
      setRuleQueue({
        proposals,
        index: 0,
        classes: (cls.data?.classes || cls.data?.items || []).map(x => ({ id: x.id, name: x.name })),
        tags:    (tg.data?.tags    || tg.data?.items    || []).map(x => ({ id: x.id, name: x.name })),
      });
    } catch (e) {
      showToast(`Suggestion failed: ${e.response?.data?.detail || e.message}`, "error");
    } finally {
      setApplying(false);
    }
  };
  return (
    <div
      data-testid={`bucket-expansion-${bucketKey}`}
      className="w-full bg-slate-50/60 rounded-b border-x border-b border-slate-200 -mt-1"
    >
      {selectedIds.size > 0 && (
        <div
          className="mx-3 mt-3 rounded-md bg-slate-900 text-white px-4 py-2.5 flex items-center gap-3 flex-wrap"
          data-testid={`bucket-bulk-bar-${bucketKey}`}
        >
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <button
            data-testid={`bucket-bulk-approve-${bucketKey}`}
            disabled={applying}
            onClick={bulkApprove}
            className="inline-flex items-center gap-1 px-3 py-1 rounded bg-white text-slate-900 text-xs font-medium"
          >
            <Check size={12} /> Approve all
          </button>
          <button
            data-testid={`bucket-bulk-update-${bucketKey}`}
            disabled={applying}
            onClick={() => setBulkUpdateOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-1 rounded bg-sky-500 text-white text-xs font-medium hover:bg-sky-400"
          >
            <SlidersHorizontal size={12} /> Bulk update
          </button>
          <button
            data-testid={`bucket-bulk-create-rules-${bucketKey}`}
            disabled={applying}
            onClick={bulkCreateRules}
            className="inline-flex items-center gap-1 px-3 py-1 rounded bg-indigo-500 text-xs font-medium"
          >
            <Wand2 size={12} /> Make these rules
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs opacity-70 hover:opacity-100"
          >
            Clear
          </button>
        </div>
      )}
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <th className="pl-4 pr-1 py-1.5 font-normal w-6">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                data-testid={`bucket-select-all-${bucketKey}`}
                title={allSelected ? "Clear selection" : "Select all rows in this bucket"}
              />
            </th>
            <th className="pr-2 py-1.5 font-normal w-[86px]">Date</th>
            <th className="px-2 py-1.5 font-normal">Description</th>
            <th className="px-2 py-1.5 font-normal w-[190px]">Contact</th>
            <th className="px-2 py-1.5 font-normal w-[240px]">Category</th>
            <th className="px-2 py-1.5 font-normal w-[100px] text-right pr-4">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} data-testid={`bucket-txn-${r.id}`} className={`border-b border-slate-100 last:border-b-0 hover:bg-white ${selectedIds.has(r.id) ? "bg-indigo-50/40" : ""}`}>
              <td className="pl-4 pr-1 py-1.5">
                <input
                  type="checkbox"
                  checked={selectedIds.has(r.id)}
                  onChange={() => toggleOne(r.id)}
                  data-testid={`bucket-select-${r.id}`}
                />
              </td>
              <td className="pr-2 py-1.5 tabular-nums text-slate-600">{r.date || "—"}</td>
              <td className="px-2 py-1.5 text-slate-800 truncate max-w-[280px]" title={r.description}>
                {r.merchant || r.description || "—"}
              </td>
              <td className="px-2 py-1.5">
                <select
                  value={r.contact_id || ""}
                  onChange={(e) => {
                    const cid = e.target.value || null;
                    const contact = contacts.find(x => x.id === cid);
                    onUpdate(r.id, {
                      contact_id: cid,
                      contact_name: contact?.name || null,
                    });
                  }}
                  className="w-full text-xs border border-slate-300 rounded px-1.5 py-1 bg-white"
                  data-testid={`bucket-row-contact-${r.id}`}
                >
                  <option value="">— none —</option>
                  {contacts.map(ct => (
                    <option key={ct.id} value={ct.id}>{ct.name}</option>
                  ))}
                </select>
              </td>
              <td className="px-2 py-1.5">
                <select
                  value={r.category_account_id || ""}
                  onChange={(e) => {
                    const aid = e.target.value || null;
                    const acct = accounts.find(x => x.id === aid);
                    onUpdate(r.id, {
                      category_account_id: aid,
                      category_account_code: acct?.code || null,
                      category_account_name: acct?.name || null,
                    });
                  }}
                  className="w-full text-xs border border-slate-300 rounded px-1.5 py-1 bg-white"
                  data-testid={`bucket-row-category-${r.id}`}
                >
                  <option value="">— uncategorized —</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                  ))}
                </select>
              </td>
              <td className={`px-2 py-1.5 text-right pr-4 tabular-nums ${(r.amount || 0) < 0 ? "text-slate-800" : "text-emerald-700"}`}>
                {r.amount != null ? `$${Math.abs(r.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {reclassOpen && (
        <ReclassifyPicker
          accounts={accounts}
          count={selectedIds.size}
          onCancel={() => setReclassOpen(false)}
          onApply={bulkReclassifyApply}
          contacts={contacts}
        />
      )}
      {contactPickerOpen && (
        <ContactPickerModal
          contacts={contacts}
          count={selectedIds.size}
          onCancel={() => setContactPickerOpen(false)}
          onApply={bulkSetContactApply}
        />
      )}
      {bulkUpdateOpen && (
        <BulkUpdateModal
          currentId={currentId}
          count={selectedIds.size}
          contacts={contacts}
          accounts={accounts}
          onCancel={() => setBulkUpdateOpen(false)}
          onApply={bulkUpdateApply}
          onContactCreated={(c) => {
            window.dispatchEvent(new CustomEvent("axiom:action",
              { detail: { kind: "contacts:changed", at: Date.now() } }));
          }}
          onAccountCreated={(a) => {
            window.dispatchEvent(new CustomEvent("axiom:action",
              { detail: { kind: "accounts:changed", at: Date.now() } }));
          }}
        />
      )}
      {pendingConfirm && (
        <BulkConfirmModal
          title={pendingConfirm.title}
          body={pendingConfirm.body}
          confirmLabel={pendingConfirm.confirmLabel}
          variant={pendingConfirm.variant}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={async () => {
            const fn = pendingConfirm.exec;
            setPendingConfirm(null);
            await fn?.();
          }}
          confirmAndRuleLabel={pendingConfirm.confirmAndRuleLabel}
          onConfirmAndRule={pendingConfirm.execAndRule ? async () => {
            const fn = pendingConfirm.execAndRule;
            setPendingConfirm(null);
            await fn?.();
          } : undefined}
        />
      )}
      {ruleQueue && (
        <CreateRuleModal
          key={`rq-${ruleQueue.index}`}
          currentId={currentId}
          accts={accounts}
          contacts={contacts}
          classes={ruleQueue.classes}
          tags={ruleQueue.tags}
          setClasses={(fn) => setRuleQueue(q =>
            q ? { ...q, classes: typeof fn === "function" ? fn(q.classes) : fn } : q)}
          setTags={(fn) => setRuleQueue(q =>
            q ? { ...q, tags:    typeof fn === "function" ? fn(q.tags)    : fn } : q)}
          initialProposal={ruleQueue.proposals[ruleQueue.index]}
          queue={{
            current: ruleQueue.index + 1,
            total:   ruleQueue.proposals.length,
            onNext: (saved) => {
              const nextIdx = ruleQueue.index + 1;
              if (nextIdx >= ruleQueue.proposals.length) {
                setRuleQueue(null);
                setSelectedIds(new Set());
                if (saved) onRefresh?.();
                showToast("Done — all suggested rules processed.");
              } else {
                setRuleQueue({ ...ruleQueue, index: nextIdx });
              }
            },
            onSkip: () => {
              const nextIdx = ruleQueue.index + 1;
              if (nextIdx >= ruleQueue.proposals.length) {
                setRuleQueue(null);
              } else {
                setRuleQueue({ ...ruleQueue, index: nextIdx });
              }
            },
            onPrev: () => {
              const prevIdx = ruleQueue.index - 1;
              if (prevIdx >= 0) {
                setRuleQueue({ ...ruleQueue, index: prevIdx });
              }
            },
          }}
          onClose={() => setRuleQueue(null)}
        />
      )}
    </div>
  );
}
